<script>
import Title from "@/components/admin/common/TitleArea.vue"
import ManualLookupDialog from "@/components/admin/logis/packing/ManualLookupDialog.vue"
import CompletionActionDialog from "@/components/admin/logis/packing/CompletionActionDialog.vue"
import ResetConfirmDialog from "@/components/admin/logis/packing/ResetConfirmDialog.vue"
import ScanLogsPanel from "@/components/admin/logis/packing/ScanLogsPanel.vue"
import FlowBanner from "@/components/admin/logis/packing/FlowBanner.vue"
import BarcodeScanCard from "@/components/admin/logis/packing/BarcodeScanCard.vue"
import OrderStartCard from "@/components/admin/logis/packing/OrderStartCard.vue"
import PickingListPanel from "@/components/admin/logis/packing/PickingListPanel.vue"
import WaybillPrintConfirmDialog from "@/components/admin/logis/packing/WaybillPrintConfirmDialog.vue"
import PdfPreview from "@/components/admin/common/modal/PdfPreview.vue"
import PackingSkuDialog from "@/components/admin/logis/packing/PackingSkuDialog.vue"

const PAGE_STATE = {
  IDLE: "IDLE",
  ORDER_SELECTED: "ORDER_SELECTED",
  PACKING_STARTED: "PACKING_STARTED",
  READY_TO_COMPLETE: "READY_TO_COMPLETE",
  WEIGHT_REQUIRED: "WEIGHT_REQUIRED",
  COMPLETED: "COMPLETED",
  ERROR: "ERROR"
}

const LOOKUP_RANGE_OPTIONS = [
  { label: "최근 1달", value: "1m" },
  { label: "최근 2달", value: "2m" }
]

const PACKING_MODE_OPTIONS = [
  { label: "일반 모드", value: "NORMAL" },
  { label: "익스프레스 모드", value: "EXPRESS" }
]

const ENV_LEVEL = String(process.env.VUE_APP_ENV_LEVEL || "").toUpperCase()
const CAN_BYPASS_EXPRESS_PRINT_FAILURE = ["LOCAL", "DEVELOPMENT"].includes(ENV_LEVEL)
const PRINT_AGENT_BASE_URL = "http://127.0.0.1:18181"
const EXPRESS_FINALIZATION_STORAGE_PREFIX = "aone:packing:express-finalization:v3"
const PRODUCT_SCAN_COOLDOWN_MS = 2000
const PRODUCT_SCAN_STORAGE_PREFIX = "aone:packing:product-scan:v1"
const EXPRESS_CLAIM_STORAGE_PREFIX = "aone:packing:express-claim:v1"

export default {
  name: "AdminPacking",
  components: {
    Title,
    ManualLookupDialog,
    CompletionActionDialog,
    ResetConfirmDialog,
    ScanLogsPanel,
    FlowBanner,
    BarcodeScanCard,
    OrderStartCard,
    PickingListPanel,
    WaybillPrintConfirmDialog,
    PdfPreview,
    PackingSkuDialog,
  },
  /**
   * Title        : 화면 상태 데이터 초기화
   * Status       : Actived
   * Description  : 포장 화면에서 사용하는 모든 UI 상태값을 초기화한다.
   */
  data() {
    return {
      description: {
        title: "포장 관리"
      },
      timezoneLabel: "Australia/Sydney",
      lookupRangePreset: "1m",
      lookupRangeOptions: LOOKUP_RANGE_OPTIONS,
      packingMode: "NORMAL",
      packingModeOptions: PACKING_MODE_OPTIONS,
      expressAvailability: {
        enabled: false,
        rules: []
      },
      expressAvailabilityLoading: false,
      selectedPartnerId: null,

      productScanBusy: false,
      productScanCoolingDown: false,
      productScanTimer: null,
      productScanDiscard: false,
      productScanNotice: '',
      productScanUncertain: null,
      productScanStopped: false,
      barcode: "",
      hblStartInput: "",
      itemBarcodeInput: "",
      hblCompleteInput: "",
      finalWeightInput: "",
      lastScannedBarcode: "",

      pageState: PAGE_STATE.IDLE,
      messageType: "info",
      messageText: "바코드를 스캔하면 미포장 오더를 자동 선택합니다.",

      selectedOrder: null,
      packingItems: [],
      waybillPrintRequested: false,
      manualLookupOpen: false,
      completionActionModalOpen: false,
      waybillPrintConfirmOpen: false,
      draftSkuConfirmOpen: false,
      draftMasterSkus: [],
      isSubmittingDraftSkuConfirm: false,
      successfulItemScanCount: 0,
      scanMilestoneConfirmOpen: false,
      scanMilestoneItem: null,
      resetConfirmOpen: false,
      manualLookupKeyword: "",
      manualLookupRows: [],
      manualLookupLoading: false,
      recentPackedLoading: false,
      isAutoPrintingWaybill: false,
      isRetryingExpressFinalization: false,
      expressScanQueue: [],
      isProcessingExpressQueue: false,
      expressFinalizationRecoveries: {},
      expressClaimRecoveries: {},
      expressClaimRuntimeId: '',
      isRetryingExpressClaimRelease: false,
      expressRecoveryTimer: null,
      expressRecoveryStopped: false,
      activeExpressOrderId: null,
      pendingExpressDraftConfirmation: null,
      skuRecoveryActive: false,
      pendingSkuResume: null,
      isResumingSku: false,
      isSelectingNormalOrder: false,
      scanLogs: [],
      isSubmittingWaybill: false,
      isStartingPacking: false,
      isRecordingProgress: false,
      isSubmittingComplete: false,
      backupCompletionOpen: false,
      backupCompletionTarget: null,
      backupCompletionWeight: "",
      backupCompletionError: "",
      isSubmittingBackupCompletion: false,
      printWaybillParcelList: [],
      audioCtx: null,
      audioUnlocked: false,
      audioWorkletNode: null,
      audioWorkletGain: null,
      audioWorkletReady: false,
      audioWorkletModuleUrl: "",

      now: "",
      timerId: null,
      isComposing: false,
    }
  },
  computed: {
    productScanBlocked() {
      return this.productScanBusy || this.productScanCoolingDown || Boolean(this.productScanUncertain)
    },
    productScanStatus() {
      if (this.productScanBusy) return '상품 스캔을 처리 중입니다. 추가 스캔은 접수하지 않습니다. 결과 확인 후 다시 스캔하세요.'
      if (this.productScanUncertain) return `상품 수량 저장 결과를 확인할 수 없습니다. 주문 ${this.productScanUncertain.orderNo || this.productScanUncertain.orderId || '-'}의 서버 수량과 실물을 관리자가 확인해야 합니다. 재스캔·수량 조정·완료를 중단하세요. 새로고침으로 해제되지 않습니다.`
      if (this.productScanCoolingDown) return '상품 스캔 결과가 반영되었습니다. 2초 동안 다음 상품 스캔을 받지 않습니다. 안내가 사라진 뒤 다시 스캔하세요.'
      return this.productScanNotice
    },
    pendingExpressClaimReleaseCount() {
      return this.readPendingExpressClaims().filter(row => row.stage === 'RELEASE_PENDING'
        && Number(row.partnerId) === Number(this.selectedPartnerId)).length
    },
    session() {
      return this.$store.getters.getLocalSession || {}
    },
    authLevel() {
      return Number(this.session.authLevel || 0)
    },
    actorName() {
      const lName = this.session.lName || ""
      const fName = this.session.fName || ""
      const fullName = `${lName}${fName}`.trim()
      return fullName || this.session.memberId || "-"
    },
    rawPartnerList() {
      return this.$store.state.storage?.partnerList || []
    },
    partnerOptions() {
      return this.rawPartnerList
        .filter((item) => Number(item?.has_fulfillment_contract) === 1)
        .map((item) => ({
          label: item.partner_name,
          value: item.id
        }))
    },
    lockedPartnerId() {
      return this.session.partnerId || this.session.partner_id || null
    },
    isPartnerLocked() {
      return this.authLevel < 90 && Boolean(this.lockedPartnerId)
    },
    isExpressAvailable() {
      return Boolean(this.expressAvailability?.enabled)
    },
    isExpressMode() {
      return this.packingMode === "EXPRESS" && this.isExpressAvailable
    },
    packingModeToggleColor() {
      return this.isExpressMode ? "orange-8" : "primary"
    },
    canBypassExpressPrintFailure() {
      return CAN_BYPASS_EXPRESS_PRINT_FAILURE
    },
    selectedPartnerOption() {
      return this.partnerOptions.find((option) => Number(option.value) === Number(this.selectedPartnerId)) || null
    },
    selectedPartnerLabel() {
      if (this.selectedPartnerOption?.label) return this.selectedPartnerOption.label
      return this.session.partner_name || this.session.partnerName || ""
    },
    partnerSelectionRequired() {
      return !this.selectedPartnerId
    },
    activeInputRef() {
      if (this.isExpressMode) return "barcodeInput"
      if (this.pageState === PAGE_STATE.ORDER_SELECTED) return "hblStartInputRef"
      if (this.pageState === PAGE_STATE.PACKING_STARTED) return "itemScanInputRef"
      if (this.pageState === PAGE_STATE.READY_TO_COMPLETE) return "hblCompleteInputRef"
      if (this.pageState === PAGE_STATE.WEIGHT_REQUIRED) return "finalWeightInputRef"
      if (this.pageState === PAGE_STATE.COMPLETED) return "barcodeInput"
      return "barcodeInput"
    },
    completedItemCount() {
      return this.packingItems.filter((item) => item.pickedQty >= item.requiredQty).length
    },
    totalItemCount() {
      return this.packingItems.length
    },
    isAllItemsCompleted() {
      return this.totalItemCount > 0 && this.completedItemCount === this.totalItemCount
    },
    canCompleteBackup() {
      return !this.productScanUncertain && Boolean(this.selectedOrder?.orderId) && this.isSelectedPartnerOrder(this.selectedOrder)
        && this.pageState !== PAGE_STATE.COMPLETED && !this.selectedOrder.packingCompleted
        && !this.isSubmittingBackupCompletion && !this.isSubmittingComplete
        && !this.isStartingPacking && !this.isSubmittingWaybill && !this.isAutoPrintingWaybill
        && !this.isRecordingProgress && !this.isProcessingExpressQueue && !this.isRetryingExpressFinalization
        && !this.draftSkuConfirmOpen && !this.draftMasterSkus.length
        && !this.skuRecoveryActive && !this.pendingSkuResume
    },
    normalizedBackupCompletionWeight() {
      const raw = String(this.backupCompletionWeight ?? "").trim()
      if (!/^\d+(?:\.\d{1,2})?$/.test(raw)) return null
      const weight = Number(raw)
      return Number.isFinite(weight) && weight > 0 && weight <= 9999.99 ? weight : null
    },
    canStartByHbl() {
      return !this.skuRecoveryActive && !this.pendingSkuResume && this.pageState === PAGE_STATE.ORDER_SELECTED
    },
    canScanItems() {
      return !this.scanMilestoneConfirmOpen
        && !this.skuRecoveryActive && !this.pendingSkuResume
        && (this.pageState === PAGE_STATE.PACKING_STARTED || this.pageState === PAGE_STATE.READY_TO_COMPLETE)
    },
    canCompletePacking() {
      return !this.productScanUncertain && !this.skuRecoveryActive && !this.pendingSkuResume && this.pageState === PAGE_STATE.READY_TO_COMPLETE && this.isAllItemsCompleted
    },
    normalizedFinalWeight() {
      const raw = String(this.finalWeightInput ?? "").trim()
      if (!/^\d+(?:\.\d{1,2})?$/.test(raw)) return null
      const weight = Number(raw)
      if (!Number.isFinite(weight) || weight <= 0 || weight > 9999.99) return null
      return weight
    },
    isFinalWeightValid() {
      return this.normalizedFinalWeight !== null
    },
    isFinalHblConfirmed() {
      return this.pageState === PAGE_STATE.WEIGHT_REQUIRED
    },
    canSubmitPackingCompletion() {
      return !this.productScanUncertain && !this.skuRecoveryActive && !this.pendingSkuResume && this.isFinalHblConfirmed && this.isFinalWeightValid && !this.isSubmittingComplete
    },
    canResetByFlow() {
      return !this.isExpressQueueContextLocked && Boolean(this.lastScannedBarcode || this.selectedOrder || this.pageState !== PAGE_STATE.IDLE)
    },
    isExpressQueueContextLocked() {
      return this.productScanBusy || Boolean(this.productScanUncertain) || this.isRecordingProgress || this.isProcessingExpressQueue || this.skuRecoveryActive || Boolean(this.pendingSkuResume)
        || this.isResumingSku || this.expressScanQueue.length > 0
    },
    isBlockingDialogOpen() {
      return this.skuRecoveryActive || this.backupCompletionOpen || this.manualLookupOpen
        || this.completionActionModalOpen
        || this.resetConfirmOpen
        || this.draftSkuConfirmOpen
        || this.scanMilestoneConfirmOpen
    },
    completeDisabledReason() {
      if (this.pageState === PAGE_STATE.COMPLETED) return "이미 완료된 오더입니다."
      if (!this.selectedOrder) return "선택된 오더가 없습니다."
      if (this.pageState === PAGE_STATE.WEIGHT_REQUIRED) {
        return this.isFinalWeightValid
          ? ""
          : "최종 무게를 0보다 큰 값으로 소수점 둘째 자리까지 입력하세요."
      }
      if (!this.canScanItems) return "HBL 1차 스캔 후 포장을 시작하세요."
      if (!this.isAllItemsCompleted) return "전 품목 필요수량 충족 후 완료할 수 있습니다."
      return ""
    },
    progressText() {
      return `완료 ${this.completedItemCount} / 전체 ${this.totalItemCount}`
    },
    lookupRangeLabel() {
      const matched = this.lookupRangeOptions.find((option) => option.value === this.lookupRangePreset)
      return matched ? matched.label : ""
    },
    filteredManualLookupRows() {
      const keyword = (this.manualLookupKeyword || "").trim().toLowerCase()
      if (!keyword) return this.manualLookupRows

      return this.manualLookupRows.filter((row) => {
        return [
          row.orderNo,
          row.hblNo,
          row.customerName,
          row.destination,
          row.firstBarcode
        ].some((value) => String(value || "").toLowerCase().includes(keyword))
      })
    },
    nextActionText() {
      if (this.pendingSkuResume) return this.messageText || '상품 확인 후 같은 주문을 재개하세요.'
      if (this.partnerSelectionRequired) return "파트너를 먼저 선택한 뒤 바코드 스캔 또는 수동조회를 진행하세요."
      if (this.messageText && (this.pageState === PAGE_STATE.ERROR
        || (this.pageState === PAGE_STATE.IDLE && ['negative', 'warning'].includes(this.messageType)))) return this.messageText
      if (this.isExpressMode) {
        if (this.pageState === PAGE_STATE.IDLE) return "상품 바코드를 스캔하면 운송장 출력 후 자동 포장완료됩니다."
        if (this.pageState === PAGE_STATE.ORDER_SELECTED) return "익스프레스 자동 출력/완료를 처리 중입니다."
        if (this.pageState === PAGE_STATE.COMPLETED) return "다음 상품 바코드를 스캔하세요."
        return "익스프레스 처리 결과를 확인하고 다음 상품 바코드를 스캔하세요."
      }
      if (this.pageState === PAGE_STATE.IDLE) return "상품 바코드를 스캔해 오더를 선택하세요."
      if (this.pageState === PAGE_STATE.ORDER_SELECTED) return "HBL 1차 스캔으로 포장을 시작하세요."
      if (this.pageState === PAGE_STATE.PACKING_STARTED) return "상품 바코드를 계속 스캔해 수량을 맞추세요."
      if (this.pageState === PAGE_STATE.READY_TO_COMPLETE) return "HBL 재스캔으로 포장을 완료하세요."
      if (this.pageState === PAGE_STATE.COMPLETED) return "다음 오더 시작 버튼으로 다음 작업을 진행하세요."
      return "오류 원인을 확인하고 다시 시도하세요."
    },
    pendingItems() {
      return this.packingItems
        .filter((item) => item.pickedQty < item.requiredQty)
        .map((item) => ({
          ...item,
          remainingQty: item.requiredQty - item.pickedQty
        }))
    },
    recentPackedOrders() {
      return this.$store.getters.getRecentPackedOrders || []
    },
    recentPackedSummary() {
      return {
        total: this.recentPackedOrders.length,
        ready: this.recentPackedOrders.filter((item) => item.tempConsolStatus === "READY").length
      }
    }
  },
  methods: {
    productScanStorageKey() {
      const memberId = this.session?.memberId ?? this.session?.id
      return memberId ? `${PRODUCT_SCAN_STORAGE_PREFIX}:${encodeURIComponent(String(memberId))}` : null
    },
    persistProductScanState(until = 0) {
      const key = this.productScanStorageKey()
      if (!key) throw new Error('작업자 정보를 확인할 수 없어 상품 스캔을 저장하지 않았습니다.')
      window.sessionStorage.setItem(key, JSON.stringify({ until, uncertain: this.productScanUncertain }))
    },
    restoreProductScanState() {
      try {
        const key = this.productScanStorageKey()
        const saved = JSON.parse((key && window.sessionStorage.getItem(key)) || 'null')
        this.productScanUncertain = saved?.uncertain || null
        const remaining = Math.min(PRODUCT_SCAN_COOLDOWN_MS, Number(saved?.until || 0) - Date.now())
        if (remaining > 0) this.startProductScanCooldown(remaining)
      } catch (err) {
        // Corrupt/unreadable recovery metadata must not silently permit a new write.
        this.productScanUncertain = { orderNo: '복구 기록 확인 필요' }
        this.$log?.warn?.('[ Packing / 상품 스캔 복구 기록 조회 실패 ]', err)
      }
    },
    rejectProductScan() {
      this.barcode = ''
      this.itemBarcodeInput = ''
      this.productScanNotice = '추가 상품 스캔은 접수되지 않았습니다. 현재 결과와 안내를 확인한 뒤 다시 스캔하세요.'
    },
    updateProductScanInput(field, value) {
      if (this.productScanBlocked || this.productScanDiscard || this.skuRecoveryActive || this.pendingSkuResume) {
        this.rejectProductScan()
        return
      }
      this[field] = value || ''
      this.productScanNotice = ''
    },
    handleProductScanKey(evt) {
      if (!this.productScanBlocked && !this.productScanDiscard && !this.skuRecoveryActive && !this.pendingSkuResume) return
      if (evt.key.length === 1 || evt.key === 'Enter' || this.productScanDiscard) {
        if (evt.type === 'keydown') this.productScanDiscard = true
        evt.preventDefault()
        evt.stopImmediatePropagation()
        this.rejectProductScan()
        // Discard the entire scanner burst, even when its tail arrives after unlock.
        if (evt.type === 'keyup' && evt.key === 'Enter') this.productScanDiscard = false
      }
    },
    beginProductScan() {
      if (this.productScanBlocked || this.productScanDiscard || this.isRecordingProgress || this.isStartingPacking
        || this.isSubmittingComplete || this.isSelectingNormalOrder || this.isProcessingExpressQueue
        || this.skuRecoveryActive || this.pendingSkuResume || this.isResumingSku || this.isBlockingDialogOpen) {
        this.rejectProductScan()
        return false
      }
      const unresolved = this.readPendingExpressFinalizations().find(row => Number(row.partnerId) === Number(this.selectedPartnerId))
      if (unresolved) {
        this.rejectProductScan()
        this.productScanNotice = '이전 미해결 기록이 있습니다. 출력·완료 저장 결과가 확인될 때까지 새 상품 스캔을 받지 않습니다. 관리자에게 확인하세요.'
        return false
      }
      this.productScanBusy = true
      this.productScanNotice = ''
      return true
    },
    startProductScanCooldown(ms = PRODUCT_SCAN_COOLDOWN_MS) {
      if (this.productScanTimer) clearTimeout(this.productScanTimer)
      this.productScanCoolingDown = true
      const deadline = performance.now() + ms
      const unlock = () => {
        const remaining = deadline - performance.now()
        if (remaining > 0) { this.productScanTimer = setTimeout(unlock, remaining); return }
        this.barcode = ''
        this.itemBarcodeInput = ''
        this.productScanCoolingDown = false
        this.productScanNotice = ''
        this.productScanTimer = null
        this.focusActiveInput()
      }
      this.productScanTimer = setTimeout(unlock, ms)
    },
    async finishProductScan() {
      this.barcode = ''
      this.itemBarcodeInput = ''
      // Flush the result and allow a paint before starting the full two seconds.
      await this.$nextTick()
      if (window.requestAnimationFrame) await new Promise(resolve => window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)))
      if (this.productScanStopped) return
      this.startProductScanCooldown()
      try { this.persistProductScanState(Date.now() + PRODUCT_SCAN_COOLDOWN_MS) }
      catch (err) { this.$log?.warn?.('[ Packing / 상품 스캔 대기 복구 기록 저장 실패 ]', err) }
      this.productScanBusy = false
      this.focusActiveInput()
    },
    isSkuResumeContextCurrent(target) {
      return Number(this.selectedOrder?.orderId) === Number(target.orderId)
        && Number(this.selectedPartnerId) === Number(target.partnerId)
        && this.isExpressMode === target.expressMode
        && (!target.rangePreset || this.lookupRangePreset === target.rangePreset)
        && (target.memberId === undefined || String(this.session?.memberId ?? this.session?.id ?? '') === target.memberId)
    },
    isExpressScanContextCurrent(scan) {
      return (scan.partnerId === undefined || Number(scan.partnerId) === Number(this.selectedPartnerId))
        && (scan.packingMode === undefined || (scan.packingMode === 'EXPRESS' && this.isExpressMode))
        && (!scan.rangePreset || scan.rangePreset === this.lookupRangePreset)
        && (scan.memberId === undefined || scan.memberId === String(this.session?.memberId ?? this.session?.id ?? ''))
    },
    queueRecoveryScan() {
      this.rejectProductScan()
    },
    startQueuedExpressScans() {
      if (!this.expressScanQueue.length || this.isProcessingExpressQueue || this.pendingSkuResume || this.skuRecoveryActive || this.isResumingSku) return this.expressQueuePromise
      this.expressQueuePromise = this.drainExpressScans()
      return this.expressQueuePromise
    },
    async ensureSelectedOrderSkus(stage, scan = null) {
      const order = this.selectedOrder
      if (!order?.orderId) return false
      const target = { orderId: order.orderId, orderNo: order.orderNo, partnerId: this.selectedPartnerId,
        partnerLabel: this.selectedPartnerLabel, expressMode: this.isExpressMode, stage, scan,
        rangePreset: scan?.rangePreset || this.lookupRangePreset,
        memberId: String(this.session?.memberId ?? this.session?.id ?? '') }
      this.pendingSkuResume = target
      this.messageType = 'info'
      this.messageText = target.expressMode
        ? '선택한 주문의 상품 기준정보·연결을 확인 중입니다. 추가 스캔은 접수하지 않습니다. 현재 주문 처리 후 다시 스캔하세요.'
        : '선택한 주문의 상품 기준정보·연결을 확인 중입니다. 확인 후 같은 주문의 포장을 이어갑니다.'
      const result = await this.$refs.skuRecovery.ensure(target)
      if (!result.ready) {
        this.messageType = 'warning'
        this.messageText = '상품 확인을 중단했습니다. 주문과 스캔 수량은 유지됩니다. ‘상품 확인 후 재개’로 이어서 처리하세요.'
        return false
      }
      if (!this.isSkuResumeContextCurrent(target)) return false
      try {
        const response = await this.$http({ method: 'POST', url: '/api/fulfillment/packing/order-state',
          data: target, timeout: 15000, skipSpinner: true })
        if (!this.isSkuResumeContextCurrent(target)) return false
        if (response?.data?.ok !== true || !response.data.data?.order) throw new Error('주문 재조회 실패')
        const selected = this.normalizeAutoSelectPayload(response.data.data)
        if (Number(selected.selectedOrder.orderId) !== Number(target.orderId)
          || Number(selected.selectedOrder.partnerId) !== Number(target.partnerId)) throw new Error('주문 정보 불일치')
        this.selectedOrder = { ...order, ...selected.selectedOrder }
        this.packingItems = this.buildPackingItems(selected.items)
        this.draftMasterSkus = selected.draftMasterSkus || []
        if (this.selectedOrder.packingCompleted) {
          this.pendingSkuResume = null
          this.pageState = PAGE_STATE.COMPLETED
          this.messageType = 'info'
          this.messageText = '이미 완료된 주문입니다. 자동 출력이나 수량 변경을 하지 않습니다.'
          return false
        }
        this.pendingSkuResume = null
        return true
      } catch (e) {
        this.messageType = 'negative'
        this.messageText = '상품 확인 후 주문 재조회에 실패했습니다. 현재 수량을 유지합니다. ‘상품 확인 후 재개’로 다시 조회하세요.'
        return false
      }
    },
    async resumeAfterSkuRecovery() {
      if (this.productScanBusy || this.productScanCoolingDown || this.productScanUncertain) { this.rejectProductScan(); return }
      if (this.isResumingSku || this.skuRecoveryActive || !this.pendingSkuResume) return
      const target = this.pendingSkuResume
      if (!this.isSkuResumeContextCurrent(target)) {
        this.messageType = 'warning'
        this.messageText = '중단된 주문·파트너·모드·조회 기간이 바뀌었습니다. 원래 컨텍스트를 확인한 뒤 현재 주문을 재개하세요. 추가 스캔은 접수되지 않았습니다.'
        return
      }
      this.productScanBusy = true
      this.isResumingSku = true
      let resumed = false
      try {
        if (!await this.ensureSelectedOrderSkus(target.stage, target.scan)) {
          resumed = !this.pendingSkuResume && this.pageState === PAGE_STATE.COMPLETED
          return
        }
        if (target.stage === 'EXPRESS') await this.completeExpressPacking({ selectedOrder: this.selectedOrder, items: this.packingItems })
        else await this.continueAfterDraftSkuConfirm()
        resumed = true
      } catch (e) {
        this.showExpressFailure(e, target.scan?.barcode || this.lastScannedBarcode)
      } finally {
        this.isResumingSku = false
        if (resumed) this.expressScanQueue = []
        await this.finishProductScan()
      }
    },
    expressFailure(err, stage, code = null) {
      const failure = err && typeof err === 'object' ? err : new Error('익스프레스 처리 실패')
      failure.expressStage = stage
      if (code) failure.expressCode = code
      return failure
    },
    expressApiFailure(response, stage) {
      const error = new Error('익스프레스 요청 실패')
      error.response = { data: response }
      return this.expressFailure(error, stage)
    },
    noOrderBarcodeNotice(status) {
      const notices = {
        UNREGISTERED: '미등록 상품 바코드입니다. 공통 상품 기준정보와 선택한 파트너 상품 연결이 없습니다. 풀필먼트 > 신규 등록에서 상품을 확인·등록하세요.',
        LINK_REQUIRED: '등록된 상품이지만 선택한 파트너의 상품 연결이 없습니다. 관리자에게 연결 확인을 요청하세요.',
        REGISTERED: '상품과 파트너 연결은 등록되어 있습니다. 선택한 파트너·조회 기간에 처리 가능한 주문이 없습니다.',
        DRAFT: '가등록된 상품입니다. 선택한 파트너·조회 기간에 처리 가능한 주문이 없습니다. 관리자에게 상품 확정 상태를 확인하세요.',
        INACTIVE: '상품 또는 선택한 파트너의 상품 연결이 비활성 상태입니다. 관리자에게 상태 확인을 요청하세요.',
        REVIEW_REQUIRED: '상품 또는 파트너 연결 정보가 충돌하거나 불완전합니다. 관리자에게 확인을 요청하세요.',
        PARTNER_UNAVAILABLE: '해당 파트너의 상품 연결을 찾을 수 없습니다. 관리자에게 상품 등록·연결 여부를 확인해 주세요.'
      }
      return Object.prototype.hasOwnProperty.call(notices, status) ? notices[status]
        : `${this.selectedPartnerLabel} / ${this.lookupRangeLabel} 내 처리 가능한 주문이 없습니다. 상품 등록 여부는 확인되지 않았습니다. 파트너·조회 기간·주문 상태를 확인하세요.`
    },
    formatExpressFailure(err) {
      const stage = err?.expressStage || 'LOOKUP'
      const payload = err?.response?.data || {}
      const rawCode = Number(err?.response?.status) >= 500 ? 'INTERNAL' : err?.expressCode || payload.code
      const code = typeof rawCode === 'string' ? rawCode : 'UNKNOWN'
      const reasons = {
        NO_EXPRESS_ELIGIBLE_ORDER: '상품 한 행, 수량 1개인 익스프레스 대상 주문이 없습니다. 나머지 주문은 일반 포장으로 진행하세요.',
        PACKING_EXPRESS_SINGLE_ITEM_REQUIRED: '익스프레스는 상품 한 행, 수량 1개인 주문만 가능합니다.',
        NO_PENDING_ORDER: '선택한 파트너·조회 기간에 처리 가능한 주문이 없습니다.',
        BARCODE_LOOKUP_UNVERIFIED: '상품 등록 여부를 확인하지 못했습니다. 다시 조회해 주세요.',
        PACKING_EXPRESS_NOT_ALLOWED: '선택한 파트너는 익스프레스 포장이 허용되지 않습니다.',
        PACKING_EXPRESS_ORDER_NOT_FOUND: '포장 대상 주문을 찾을 수 없습니다.',
        PACKING_EXPRESS_ITEM_INVALID: '주문 상품 또는 바코드 정보가 올바르지 않습니다.',
        PACKING_EXPRESS_SKU_NOT_FOUND: '상품 기준정보 또는 파트너 상품 연결이 없습니다.',
        PACKING_EXPRESS_DRAFT_SKU: '가등록 상품의 스펙·중량 확정이 필요합니다.',
        PACKING_EXPRESS_RULE_MISMATCH: '상품이 허용 브랜드·카테고리 범위에 포함되지 않습니다.',
        PACKING_EXPRESS_WEIGHT_INVALID: '상품 중량·수량이 없거나 허용 범위를 벗어났습니다.',
        PACKING_EXPRESS_PRINTED_WEIGHT_REQUIRED: '접수한 운송장 중량을 확인할 수 없습니다.',
        PACKING_EXPRESS_WEIGHT_CHANGED: '운송장 접수 후 상품의 계산 중량이 변경되었습니다.',
        PACKING_ORDER_ALREADY_CONSOLIDATED: '이미 콘솔에 연결된 주문입니다.',
        PACKING_SEQUENCE_INVALID: '주문의 포장 진행 상태가 요청과 일치하지 않습니다.',
        PACKING_OWNER_CHANGED: '포장 주체가 변경되었습니다. 통합조회에서 현재 주체를 확인해 주세요.',
        PACKING_REQUEST_REJECTED: '주문 상태 또는 작업 권한으로 요청이 거절되었습니다.',
        UNAUTHORIZED: '로그인 상태 또는 작업 권한을 확인해야 합니다.',
        FORBIDDEN_CONTRACT_REQUIRED: '풀필먼트 계약 또는 작업 권한을 확인해야 합니다.',
        VALIDATION: '주문·파트너·상품 입력 정보를 확인해야 합니다.',
        NOT_FOUND: '대상 주문을 찾을 수 없습니다.',
        PRINT_REJECTED: '프린트 에이전트가 출력 요청을 거절했습니다.',
        PRINT_UNKNOWN: '출력 요청의 접수 여부를 확인할 수 없습니다.',
        PRINT_PREPARATION_FAILED: '출력할 운송장 파일을 준비하지 못했습니다.',
        FINALIZE_PENDING: '이전 출력 접수건의 포장완료 저장이 남아 있습니다.',
        CONTEXT_CHANGED: '처리 중 파트너 또는 대상 주문이 변경되었습니다.'
      }
      const labels = { LOOKUP: '주문 조회', WAYBILL: '운송장 준비', PRINT: '출력 접수', FINALIZE: '포장완료 저장', RECOVERY: '이전 미해결 기록', DRAFT: '상품 확정' }
      const defaults = { LOOKUP: '주문 조회에 실패했습니다.', WAYBILL: '운송장 정보를 준비하지 못했습니다.', PRINT: '출력 결과를 확인할 수 없습니다.', FINALIZE: '출력은 접수됐으나 포장완료 저장에 실패했습니다.', RECOVERY: '이전 출력 기록을 확인해야 합니다.', DRAFT: '상품 확정에 실패했습니다.' }
      let action = '연결 상태와 선택한 파트너·상품 정보를 확인한 뒤 다시 시도하세요.'
      if (code === 'PACKING_EXPRESS_SINGLE_ITEM_REQUIRED') action = stage === 'FINALIZE'
        ? '출력 기록을 보존했습니다. 재출력하지 말고 관리자에게 완료 복구를 요청하세요.'
        : '이 주문은 일반 포장으로 진행하세요. 다음 익스프레스 상품을 스캔할 수 있습니다.'
      else if (code === 'NO_EXPRESS_ELIGIBLE_ORDER') action = '일반 포장이 필요한 주문은 그대로 유지됩니다. 다음 상품 바코드를 스캔하세요.'
      else if (code === 'NO_PENDING_ORDER') action = '파트너·조회 기간·주문 상태를 확인하세요. 출력 요청은 하지 않았습니다.'
      else if (stage === 'FINALIZE' || code === 'FINALIZE_PENDING') action = '재출력하지 마세요. 같은 주문의 완료 저장만 자동 재시도합니다. 계속 실패하면 관리자에게 문의하세요.'
      else if (stage === 'PRINT' || stage === 'RECOVERY') action = '다시 스캔하거나 재출력하지 마세요. 프린터 대기열·실물과 관리자 확인이 필요합니다.'
      else if (code.startsWith('PACKING_EXPRESS_')) action = '관리자에게 상품 기준정보·스펙·중량·허용규칙 확인을 요청하세요. 출력 요청은 하지 않았습니다.'
      const reason = code === 'NO_PENDING_ORDER' ? this.noOrderBarcodeNotice(err?.barcodeStatus)
        : code === 'INTERNAL' ? '서버 처리 중 오류가 발생했습니다.' : (Object.prototype.hasOwnProperty.call(reasons, code) ? reasons[code] : defaults[stage] || defaults.LOOKUP)
      const requestId = /^[0-9a-f-]{36}$/i.test(payload.requestId || '') ? ` / 문의번호: ${payload.requestId}` : ''
      return `[${labels[stage] || labels.LOOKUP}] ${reason} / 다음 행동: ${action}${requestId}`
    },
    showExpressFailure(err, input, type = 'BARCODE_SCAN') {
      this.pageState = PAGE_STATE.ERROR
      this.messageType = 'negative'
      this.messageText = this.formatExpressFailure(err)
      this.addScanLog(type, input, 'FAIL', this.messageText)
    },
    normalizeManualLookupRows(rows = []) {
      if (!Array.isArray(rows)) return []

      return rows.map((row, idx) => {
        const orderNo = row.orderNo || row.order_no || row.order_id || `ORDER-${idx + 1}`
        const orderId = Number(row.orderId || row.order_base_id || 0) || null
        const partnerId = Number(row.partnerId || row.partner_id || 0) || null
        const hblNo = row.hblNo || row.hbl_no || "-"
        const customerName = row.customerName || row.customer_name || "-"
        const destination = row.destination || row.dest || "-"
        const createdAt = row.createdAt || row.created_at || "-"
        const firstBarcode = row.firstBarcode || row.first_barcode || "-"
        const selectionReason = row.selectionReason || row.selection_reason || "수동조회 선택"
        const rawItems = Array.isArray(row.items) ? row.items : []
        const items = rawItems.map((item, i) => ({
          id: item.id || item.item_id || i + 1,
          name: item.name || item.item_name || "-",
          orderItemName: item.orderItemName || item.order_item_name || item.name || item.item_name || "-",
          barcode: item.barcode || item.item_barcode || "-",
          requiredQty: Number(item.requiredQty || item.required_qty || 0),
          pickedQty: Number(item.pickedQty || item.picked_qty || 0),
          orderItemWeight: item.orderItemWeight ?? item.order_item_weight ?? null,
          fulfillmentWeight: item.fulfillmentWeight ?? item.fulfillment_weight ?? null,
          effectiveWeight: item.effectiveWeight ?? item.effective_weight ?? null,
          weightSource: item.weightSource || item.weight_source || "ORDER_ITEM",
          masterSkuId: item.masterSkuId || item.master_sku_id || null,
          masterSkuCode: item.masterSkuCode || item.master_sku_code || "",
          fulfillmentLengthCm: item.fulfillmentLengthCm ?? item.fulfillment_length_cm ?? null,
          fulfillmentWidthCm: item.fulfillmentWidthCm ?? item.fulfillment_width_cm ?? null,
          fulfillmentHeightCm: item.fulfillmentHeightCm ?? item.fulfillment_height_cm ?? null,
          fulfillmentVolumeCbm: item.fulfillmentVolumeCbm ?? item.fulfillment_volume_cbm ?? null
        }))

        return {
          orderNo,
          orderId,
          partnerId,
          hblNo,
          customerName,
          destination,
          createdAt,
          firstBarcode,
          selectionReason,
          workflowState: {
            started: Boolean(row.workflowState?.started || row.workflow_state?.started),
            finalHblConfirmed: Boolean(row.workflowState?.finalHblConfirmed || row.workflow_state?.finalHblConfirmed)
          },
          items,
          draftMasterSkus: this.normalizeDraftMasterSkus(row.draftMasterSkus || row.draft_master_skus)
        }
      })
    },
    normalizeDraftMasterSkus(rows = []) {
      if (!Array.isArray(rows)) return []
      const seen = new Set()
      return rows
        .map((row) => ({
          masterSkuId: row.masterSkuId || row.master_sku_id || null,
          masterSkuCode: row.masterSkuCode || row.master_sku_code || "-",
          barcode: row.barcode || "-",
          displayName: row.displayName || row.display_name || "-",
          registrationStatus: row.registrationStatus || row.registration_status || "DRAFT",
          specType: row.specType || row.spec_type || "PRODUCT_OPTION",
          weightKg: row.weightKg || row.weight_kg || "",
          volumeCbm: row.volumeCbm || row.volume_cbm || "",
          lengthCm: row.lengthCm || row.length_cm || "",
          widthCm: row.widthCm || row.width_cm || "",
          heightCm: row.heightCm || row.height_cm || "",
          specForm: {
            model_code: row.modelCode || row.model_code || row.spec_detail?.model_code || "",
            color: row.color || row.spec_detail?.color || "",
            size: row.sizeValue || row.size_value || row.spec_detail?.size || "",
            weight_kg: row.weightKg || row.weight_kg || "",
            volume_cbm: row.volumeCbm || row.volume_cbm || "",
            length_cm: row.lengthCm || row.length_cm || "",
            width_cm: row.widthCm || row.width_cm || "",
            height_cm: row.heightCm || row.height_cm || ""
          }
        }))
        .filter((row) => {
          const key = `${row.masterSkuId || ""}-${row.barcode}`
          if (seen.has(key)) return false
          seen.add(key)
          return true
        })
    },
    normalizeAutoSelectPayload(payload = {}) {
      const order = payload.order || {}
      const rawItems = Array.isArray(payload.items) ? payload.items : []

      const selectedOrder = {
        orderId: order.orderId || null,
        orderNo: order.orderNo || "-",
        partnerId: order.partnerId || this.selectedPartnerId || null,
        hblNo: order.hblNo || "-",
        customerName: order.customerName || "-",
        destination: order.destination || "-",
        createdAt: order.createdAt || "-",
        packingResetId: String(order.packingResetId || "0"),
        packingStatus: order.packingStatus || "READY",
        waybillPrinted: Boolean(order.waybillPrinted),
        packingCompleted: Boolean(order.packingCompleted),
        scanCase: order.scanCase || "UNPRINTED_OPEN",
        selectionReason: order.selectionReason || "오늘/미포장/등록순",
        workflowState: {
          started: Boolean(payload.workflowState?.started || payload.workflow_state?.started),
          finalHblConfirmed: Boolean(payload.workflowState?.finalHblConfirmed || payload.workflow_state?.finalHblConfirmed)
        }
      }

      const items = rawItems.map((item, idx) => ({
        id: item.orderItemId || item.id || idx + 1,
        name: item.itemName || item.name || "-",
        orderItemName: item.orderItemName || item.name || item.itemName || "-",
        barcode: item.barcode || "-",
        requiredQty: Number(item.requiredQty || 0),
        pickedQty: Number(item.pickedQty || 0),
        orderItemWeight: item.orderItemWeight ?? null,
        fulfillmentWeight: item.fulfillmentWeight ?? null,
        effectiveWeight: item.effectiveWeight ?? null,
        weightSource: item.weightSource || "ORDER_ITEM",
        masterSkuId: item.masterSkuId || null,
        masterSkuCode: item.masterSkuCode || "",
        fulfillmentLengthCm: item.fulfillmentLengthCm ?? null,
        fulfillmentWidthCm: item.fulfillmentWidthCm ?? null,
        fulfillmentHeightCm: item.fulfillmentHeightCm ?? null,
        fulfillmentVolumeCbm: item.fulfillmentVolumeCbm ?? null
      }))

      return {
        selectedOrder,
        items,
        draftMasterSkus: this.normalizeDraftMasterSkus(payload.draftMasterSkus || payload.draft_master_skus)
      }
    },
    async fetchManualLookupRows(keyword = "") {
      const config = {
        method: "POST",
        url: "/api/fulfillment/packing/manual-lookup",
        data: {
          keyword: keyword || "",
          rangePreset: this.lookupRangePreset,
          partnerId: this.selectedPartnerId
        },
        name: "[ AXIOS / 포장 / 수동조회 ]"
      }

      const res = await this.$http(config)
      const payload = res?.data?.data ?? res?.data ?? []
      return this.normalizeManualLookupRows(payload)
    },
    async autoSelectPacking(barcode, scan = null) {
      const config = {
        method: "POST",
        url: "/api/fulfillment/packing/auto-select",
        ...((scan || this.isExpressMode) ? { timeout: 15000 } : {}),
        data: {
          barcode,
          rangePreset: scan?.rangePreset || this.lookupRangePreset,
          partnerId: scan?.partnerId || this.selectedPartnerId,
          expressMode: scan?.packingMode ? scan.packingMode === 'EXPRESS' : this.isExpressMode,
        },
        name: "[ AXIOS / 포장 / 자동선택 ]"
      }

      const res = await this.$http(config)
      const ok = res?.data?.ok
      if (ok !== true) {
        throw this.expressApiFailure(res?.data, 'LOOKUP')
      }

      const payload = res?.data?.data ?? null
      const barcodeStatus = res?.data?.barcodeStatus
      if (!payload && res?.data?.code !== 'NO_EXPRESS_ELIGIBLE_ORDER' && (res?.data?.code !== 'NO_PENDING_ORDER' || (barcodeStatus !== undefined
        && !['UNREGISTERED', 'LINK_REQUIRED', 'REGISTERED', 'DRAFT', 'INACTIVE', 'REVIEW_REQUIRED', 'PARTNER_UNAVAILABLE'].includes(barcodeStatus)))) {
        throw this.expressFailure(new Error('상품 등록 여부를 확인하지 못했습니다.'), 'LOOKUP', 'BARCODE_LOOKUP_UNVERIFIED')
      }
      return {
        code: res?.data?.code || "AUTO_SELECT_OK",
        message: res?.data?.message || "",
        barcodeStatus,
        selected: payload ? this.normalizeAutoSelectPayload(payload) : null
      }
    },
    async fetchWaybillPayload(orderId, { expressMode = false } = {}) {
      const config = {
        method: "POST",
        url: "/api/fulfillment/packing/waybill-payload",
        ...(expressMode ? { timeout: 15000 } : {}),
        data: {
          id: orderId,
          packingResetId: String(this.selectedOrder?.packingResetId || "0"),
          partnerId: this.selectedPartnerId,
          expressMode
        },
        name: "[ AXIOS / 포장 / 운송장 출력 payload ]"
      }

      const res = await this.$http(config)
      const ok = res?.data?.ok
      if (ok === false) throw this.expressApiFailure(res?.data, 'WAYBILL')
      return res?.data?.data ?? null
    },
    normalizeWaybillPayload(payload = {}) {
      return {
        id: payload.orderId || payload.id || null,
        orderId: payload.orderNo || payload.orderId || "",
        hblNo: payload.hblNo || "",
        shipperName: payload.shipperName || "",
        shippingAgencyName: payload.shippingAgencyName || "",
        partnerName: payload.partnerName || "",
        shipperMobile: payload.shipperMobile || "",
        shipperAddress: payload.shipperAddress || "",
        receiverZipcode: payload.receiverZipcode || "",
        receiverAddress1: payload.receiverAddress1 || "",
        receiverAddress2: payload.receiverAddress2 || "",
        receiverName: payload.receiverName || "",
        receiverMobile: payload.receiverMobile || "",
        orderMemo: payload.orderMemo || "",
        itemNames: payload.itemNames || "",
        boxQty: payload.boxQty || "",
        weight: payload.weight || ""
      }
    },
    async printWaybillViaAgent(payload) {
      let pdfBase64
      try { pdfBase64 = await this.createWaybillPdfBase64(payload) }
      catch (err) { throw this.expressFailure(err, 'PRINT', 'PRINT_PREPARATION_FAILED') }
      const agentFetch = this.isExpressMode ? this.fetchExpressAgent : fetch
      const response = await agentFetch(`${PRINT_AGENT_BASE_URL}/print/pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          pdfBase64,
          orderNo: payload.orderNo || payload.orderId || "",
          hblNo: payload.hblNo || ""
        })
      })

      const data = await response.json().catch(() => ({}))
      if (!response.ok || data?.ok !== true) {
        throw this.expressFailure(new Error('프린트 요청 실패'), 'PRINT', response.status >= 400 && response.status < 500 ? 'PRINT_REJECTED' : 'PRINT_UNKNOWN')
      }
      return data
    },
    async createWaybillPdfBase64(payload) {
      const waybillPayload = this.normalizeWaybillPayload(payload)
      this.printWaybillParcelList = []
      await this.$nextTick()
      this.printWaybillParcelList = [waybillPayload]
      await this.$nextTick()

      const preview = this.$refs.packingWaybillPreview
      if (!preview) {
        throw new Error("운송장 출력 영역을 생성할 수 없습니다.")
      }
      const pdf = await preview.createWaybillPdf()
      return pdf.output("datauristring")
    },
    async dispatchWaybillPrint(payload) {
      return this.printWaybillViaAgent(payload)
    },
    async autoPrintWaybillForSelectedOrder(order, successDetail = "스캔 직후 자동 출력") {
      return this.printWaybillForOrder(order, {
        successDetail,
        force: false
      })
    },
    async printWaybillForOrder(order, { successDetail = "운송장 출력" } = {}) {
      if (!order?.orderId || this.isAutoPrintingWaybill) return false
      if (!this.isSelectedPartnerOrder(order)) throw new Error(this.getPartnerMismatchMessage())

      const claimRecord = {
        orderId: Number(order.orderId),
        packingResetId: String(order.packingResetId || "0"),
        partnerId: Number(order.partnerId || this.selectedPartnerId),
        claimToken: this.createPrintClaimToken()
      }
      let claimAcquired = false
      let dispatchStarted = false
      try {
        this.isAutoPrintingWaybill = true
        const claim = await this.requestWaybillPrintClaim(claimRecord)
        if (claim.alreadyPrinted) {
          this.waybillPrintRequested = true
          this.addScanLog("WAYBILL_PRINT", order.orderNo, "SKIP", "이미 출력 처리된 오더")
          return false
        }
        if (claim.claimToken !== claimRecord.claimToken) {
          throw new Error("서버 claim 토큰이 요청 토큰과 일치하지 않습니다.")
        }
        claimAcquired = true

        const payload = await this.fetchWaybillPayload(order.orderId)
        if (!payload) throw new Error("waybill payload missing")
        if (payload.waybillPrinted) {
          this.waybillPrintRequested = true
          this.addScanLog("WAYBILL_PRINT", order.orderNo, "SKIP", "이미 출력 처리된 오더")
          return false
        }

        dispatchStarted = true
        await this.dispatchWaybillPrint(payload)
        await this.requestWaybillPrint(order.orderId, claimRecord.claimToken, claimRecord.packingResetId)
        this.waybillPrintRequested = true
        this.addScanLog("WAYBILL_PRINT", order.orderNo, "SUCCESS", successDetail)
        return true
      } catch (err) {
        if (claimAcquired && !dispatchStarted) {
          await this.releaseWaybillPrintClaim(claimRecord).catch((releaseErr) => {
            this.$log?.warn?.("[ Packing / 출력 전 실패 claim 해제 실패 ]", releaseErr)
          })
        }
        throw err
      } finally {
        this.isAutoPrintingWaybill = false
      }
    },
    async printExpressWaybillForOrder(order) {
      if (!order?.orderId) throw new Error("익스프레스 출력 대상 오더가 없습니다.")
      if (this.isAutoPrintingWaybill) throw new Error("다른 운송장 출력이 진행 중입니다.")
      if (!this.isSelectedPartnerOrder(order)) throw new Error(this.getPartnerMismatchMessage())
      const claimRecord = { orderId: Number(order.orderId),
        packingResetId: String(order.packingResetId || "0"),
        partnerId: Number(order.partnerId || this.selectedPartnerId), claimToken: this.createPrintClaimToken() }
      let claimAttempted = false
      let claimAcquired = false
      let dispatchStarted = false
      try {
        this.isAutoPrintingWaybill = true
        // Before dispatch, a lost claim response can only require release, never printing.
        this.persistExpressClaim(claimRecord, 'CLAIMING')
        claimAttempted = true
        const claim = await this.requestWaybillPrintClaim(claimRecord)
        if (!claim.alreadyPrinted) {
          if (claim.claimToken !== claimRecord.claimToken) throw new Error('출력 선점 응답을 확인할 수 없습니다.')
          claimAcquired = true
        } else {
          claimAttempted = false
          this.clearExpressClaim(claimRecord)
        }
        let payload
        try { payload = await this.fetchWaybillPayload(order.orderId, { expressMode: true }) }
        catch (err) { throw this.expressFailure(err, 'WAYBILL') }
        if (!payload || !Number.isFinite(Number(payload.weight)) || Number(payload.weight) <= 0) {
          throw this.expressFailure(new Error("운송장 상품 무게를 확인할 수 없습니다."), 'WAYBILL', 'PACKING_EXPRESS_WEIGHT_INVALID')
        }
        if (claim.alreadyPrinted && !payload.waybillPrinted) throw new Error('출력 상태가 변경되었습니다. 다시 확인해 주세요.')
        if (payload.waybillPrinted) return { printed: false, weight: Number(payload.weight) }
        // Persist before dispatch so an ambiguous response cannot trigger automatic reprinting.
        this.queuePendingExpressFinalization({ ...order, ...claimRecord, stage: 'PRINTING', printedWeight: Number(payload.weight) })
        this.clearExpressClaim(claimRecord)
        dispatchStarted = true
        try { await this.dispatchWaybillPrint(payload) }
        catch (err) { throw this.expressFailure(err, 'PRINT', err.expressCode || 'PRINT_UNKNOWN') }
        this.waybillPrintRequested = true
        this.addScanLog("WAYBILL_PRINT", order.orderNo, "SUCCESS", "익스프레스 운송장 출력 요청 접수 (실물 출력은 별도 확인)")
        return { printed: true, weight: Number(payload.weight) }
      } catch (err) {
        const beforeOutput = !dispatchStarted || err?.expressCode === 'PRINT_PREPARATION_FAILED'
        const knownClaimRejection = !claimAcquired && err?.response?.status >= 400 && err.response.status < 500
        if (knownClaimRejection) this.clearExpressClaim(claimRecord)
        else if (claimAttempted && beforeOutput) {
          this.persistExpressClaim(claimRecord, 'RELEASE_PENDING')
          try {
            await this.releaseWaybillPrintClaim(claimRecord)
            this.clearExpressClaim(claimRecord)
            this.removePendingExpressFinalization(order.orderId, claimRecord.claimToken)
          } catch (releaseErr) {
            this.$log?.warn?.('[ Packing / 출력 전 선점 해제 확인 필요 ]', releaseErr)
          }
        }
        throw err
      } finally {
        this.isAutoPrintingWaybill = false
      }
    },
    async printWaybillAfterConfirm() {
      if (this.skuRecoveryActive || this.pendingSkuResume) return
      if (!this.selectedOrder) return
      if (this.isSubmittingWaybill) return

      this.waybillPrintConfirmOpen = false

      try {
        this.isSubmittingWaybill = true
        const printed = await this.printWaybillForOrder(this.selectedOrder, {
          successDetail: "수동 출력",
          force: true
        })
        this.messageType = printed ? "positive" : "info"
        this.messageText = printed
          ? "운송장 출력 요청이 접수되었습니다."
          : "운송장 출력이 취소되었거나 이미 처리된 상태입니다."
      } catch (err) {
        this.waybillPrintRequested = false
        this.messageType = "negative"
        this.messageText = "로컬 중계프로그램 운송장 출력에 실패했습니다. 중계프로그램 실행 상태와 프린터 설정을 확인해 주세요."
        this.addScanLog("WAYBILL_PRINT", this.selectedOrder.orderNo, "FAIL", err?.message || "중계프로그램 출력 실패")
        this.$log?.warn?.("[ Packing / 운송장 중계프로그램 출력 실패 ]", err)
      } finally {
        this.isSubmittingWaybill = false
        this.focusActiveInput()
      }
    },
    cancelWaybillPrintConfirm() {
      this.waybillPrintConfirmOpen = false
      this.messageType = "info"
      this.messageText = "운송장 출력을 취소했습니다."
      if (this.selectedOrder?.orderNo) {
        this.addScanLog("WAYBILL_PRINT", this.selectedOrder.orderNo, "SKIP", "사용자 취소")
      }
      this.focusActiveInput()
    },
    async requestWaybillPrintClaim({ orderId, partnerId, claimToken, packingResetId = "0" }) {
      const res = await this.$http({
        method: "POST",
        url: "/api/fulfillment/packing/waybill-print-claim",
        timeout: 15000,
        data: { id: Number(orderId), partnerId: Number(partnerId), claimToken, packingResetId },
        name: "[ AXIOS / 포장 / 운송장 인쇄 claim ]"
      })
      const response = res?.data || {}
      if (response.ok !== true) throw new Error(response.message || "waybill print claim failed")
      return response.data || {}
    },
    async releaseWaybillPrintClaim({ orderId, partnerId, claimToken, packingResetId = "0" }) {
      const res = await this.$http({
        method: "POST",
        url: "/api/fulfillment/packing/waybill-print-release",
        timeout: 15000,
        data: { id: Number(orderId), partnerId: Number(partnerId), claimToken, packingResetId },
        name: "[ AXIOS / 포장 / 운송장 인쇄 claim 해제 ]"
      })
      const response = res?.data || {}
      if (response.ok !== true) throw new Error(response.message || "waybill print claim release failed")
      return response.data || {}
    },
    async requestWaybillPrint(orderId, claimToken, packingResetId = "0") {
      const config = {
        method: "POST",
        url: "/api/fulfillment/packing/waybill-print",
        data: {
          id: orderId,
          packingResetId,
          partnerId: this.selectedPartnerId,
          claimToken
        },
        name: "[ AXIOS / 포장 / 운송장 출력요청 ]"
      }

      const res = await this.$http(config)
      if (res?.data?.ok !== true) throw new Error(res?.data?.message || "waybill print finalize failed")
    },
    getExpressFinalizationStorageKey(orderId = null) {
      const memberId = this.session?.memberId ?? this.session?.id
      if (!memberId) return null
      const prefix = `${EXPRESS_FINALIZATION_STORAGE_PREFIX}:${encodeURIComponent(String(memberId))}:`
      return orderId === null ? prefix : `${prefix}${Number(orderId)}`
    },
    readPendingExpressFinalizations() {
      const records = {}
      const prefix = this.getExpressFinalizationStorageKey()
      if (!prefix) return []
      try {
        for (let i = 0; i < window.localStorage.length; i += 1) {
          const key = window.localStorage.key(i)
          if (!key?.startsWith(prefix)) continue
          try {
            const row = JSON.parse(window.localStorage.getItem(key))
            if (Number(row?.orderId) > 0) records[row.orderId] = row
          } catch (err) { this.$log?.warn?.('[ Packing / 복구 기록 오류 ]', err) }
        }
      } catch (err) { this.$log?.warn?.('[ Packing / 복구 저장소 조회 실패 ]', err) }
      return Object.values({ ...records, ...this.expressFinalizationRecoveries })
    },
    queuePendingExpressFinalization(row) {
      const record = { ...row, partnerId: row.partnerId || this.selectedPartnerId,
        printInitiatorMemberId: row.printInitiatorMemberId ?? this.session?.memberId ?? this.session?.id ?? null }
      this.expressFinalizationRecoveries[record.orderId] = record
      const key = this.getExpressFinalizationStorageKey(record.orderId)
      if (!key) return false
      try {
        window.localStorage.setItem(key, JSON.stringify(record))
        return true
      } catch (err) {
        this.$log?.warn?.('[ Packing / 메모리 복구 사용, 종료시 복구 제한 ]', err)
        return false
      }
    },
    removePendingExpressFinalization(orderId, claimToken = null) {
      if (!claimToken || this.expressFinalizationRecoveries[orderId]?.claimToken === claimToken) {
        delete this.expressFinalizationRecoveries[orderId]
      }
      const key = this.getExpressFinalizationStorageKey(orderId)
      try {
        const stored = key ? JSON.parse(window.localStorage.getItem(key) || 'null') : null
        if (key && (!claimToken || stored?.claimToken === claimToken)) window.localStorage.removeItem(key)
      }
      catch (err) { this.$log?.warn?.('[ Packing / 복구 기록 삭제 실패 ]', err) }
    },
    getExpressClaimPrefix() {
      const memberId = this.session?.memberId ?? this.session?.id
      return memberId ? `${EXPRESS_CLAIM_STORAGE_PREFIX}:${encodeURIComponent(String(memberId))}:` : null
    },
    getExpressClaimRuntimeId() {
      if (!this.expressClaimRuntimeId) this.expressClaimRuntimeId = this.createPrintClaimToken()
      return this.expressClaimRuntimeId
    },
    persistExpressClaim(record, stage) {
      const value = { ...record, stage, runtimeId: this.getExpressClaimRuntimeId() }
      this.expressClaimRecoveries[value.claimToken] = value
      const prefix = this.getExpressClaimPrefix()
      try {
        if (prefix) window.localStorage.setItem(`${prefix}${value.runtimeId}:${value.orderId}:${value.claimToken}`, JSON.stringify(value))
      } catch (err) { this.$log?.warn?.('[ Packing / 출력 준비 기록 저장 실패 ]', err) }
    },
    readPendingExpressClaims() {
      const rows = {}, prefix = this.getExpressClaimPrefix()
      try {
        if (prefix) for (let i = 0; i < window.localStorage.length; i++) {
          const key = window.localStorage.key(i)
          if (!key?.startsWith(prefix)) continue
          try {
            const value = JSON.parse(window.localStorage.getItem(key))
            if (value?.claimToken && value?.runtimeId && Number(value.orderId) > 0
              && ['CLAIMING', 'RELEASE_PENDING'].includes(value.stage)) rows[value.claimToken] = value
          } catch (_) { /* Ignore malformed browser-only recovery metadata. */ }
        }
      } catch (err) { this.$log?.warn?.('[ Packing / 출력 준비 기록 조회 실패 ]', err) }
      return Object.values({ ...rows, ...this.expressClaimRecoveries })
    },
    clearExpressClaim(record) {
      const runtimeId = record.runtimeId || this.getExpressClaimRuntimeId()
      const memory = this.expressClaimRecoveries[record.claimToken]
      if (memory?.runtimeId === runtimeId) delete this.expressClaimRecoveries[record.claimToken]
      const prefix = this.getExpressClaimPrefix()
      try {
        if (prefix) window.localStorage.removeItem(`${prefix}${runtimeId}:${record.orderId}:${record.claimToken}`)
      } catch (err) { this.$log?.warn?.('[ Packing / 출력 준비 기록 정리 실패 ]', err) }
    },
    async retryPendingExpressClaimReleases({ adopt = false } = {}) {
      if (this.isRetryingExpressClaimRelease) return
      this.isRetryingExpressClaimRelease = true
      try {
        for (const row of this.readPendingExpressClaims()) {
          if (row.stage !== 'RELEASE_PENDING' || Number(row.partnerId) !== Number(this.selectedPartnerId)
            || Number(this.activeExpressOrderId) === Number(row.orderId)
            || (!adopt && row.runtimeId !== this.getExpressClaimRuntimeId())) continue
          try {
            await this.releaseWaybillPrintClaim(row)
            this.clearExpressClaim(row)
            this.removePendingExpressFinalization(row.orderId, row.claimToken)
          } catch (err) { this.$log?.warn?.('[ Packing / 출력 전 선점 해제 재확인 실패 ]', err) }
        }
      } finally { this.isRetryingExpressClaimRelease = false }
    },
    createPrintClaimToken() {
      const cryptoApi = window.crypto
      if (typeof cryptoApi?.randomUUID === "function") return cryptoApi.randomUUID()
      if (typeof cryptoApi?.getRandomValues === "function") {
        const bytes = new Uint8Array(16)
        cryptoApi.getRandomValues(bytes)
        bytes[6] = (bytes[6] & 0x0f) | 0x40
        bytes[8] = (bytes[8] & 0x3f) | 0x80
        const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("")
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
      }
      throw new Error("안전한 인쇄 claim 토큰을 생성할 수 없습니다.")
    },
    async requestExpressFinalization({ orderId, partnerId, printedWeight, printInitiatorMemberId = null, packingResetId = "0" }) {
      const res = await this.$http({
        method: "POST",
        url: "/api/fulfillment/packing/express-finalize",
        timeout: 15000,
        data: {
          id: Number(orderId),
          partnerId: Number(partnerId),
          printInitiatorMemberId,
          packingResetId,
          ...(Number.isFinite(Number(printedWeight)) && Number(printedWeight) > 0
            ? { printedWeight: Number(printedWeight) }
            : {})
        },
        name: "[ AXIOS / 포장 / 익스프레스 원자적 최종화 ]"
      })
      const response = res?.data || {}
      if (response.ok !== true) throw this.expressApiFailure(response, 'FINALIZE')
      const result = response.data || {}
      if (result.state?.packingStatus !== 'COMPLETED' || result.state?.waybillPrinted !== true
        || !['READY', 'LINKED'].includes(result.state?.tempConsolStatus)) {
        throw new Error("익스프레스 최종화 응답 상태가 완전하지 않습니다.")
      }
      return result
    },
    isRecoverableExpressFinalization(record) {
      return record?.stage === 'FINALIZE_PENDING' && Number(record.orderId) > 0
        && Number.isFinite(Number(record.printedWeight)) && Number(record.printedWeight) > 0
    },
    expressDelay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },
    async fetchExpressAgent(url, options = {}) {
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), 15000)
      try { return await fetch(url, { ...options, signal: controller.signal }) }
      finally { clearTimeout(timeout) }
    },
    async retryPendingExpressFinalizations() {
      if (this.isRetryingExpressFinalization || this.backupCompletionOpen || this.isSubmittingBackupCompletion) return
      this.isRetryingExpressFinalization = true
      let recovered = false
      try {
        await this.retryPendingExpressClaimReleases()
        for (const row of this.readPendingExpressFinalizations()) {
          if (Number(row.partnerId) !== Number(this.selectedPartnerId)
            || Number(this.activeExpressOrderId) === Number(row.orderId)
            ) continue
          if (!this.isRecoverableExpressFinalization(row)) continue
          try {
            await this.requestExpressFinalization(row)
            this.removePendingExpressFinalization(row.orderId)
            recovered = true
            this.addScanLog('EXPRESS_RECOVERY', row.orderNo, 'SUCCESS', '출력 접수건 완료 자동 복구')
            if (Number(this.selectedOrder?.orderId) === Number(row.orderId)) {
              this.pageState = PAGE_STATE.IDLE
              this.selectedOrder = null
              this.packingItems = []
              this.waybillPrintRequested = false
              this.messageType = 'positive'
              this.messageText = `${row.orderNo} 출력 접수건의 포장완료 저장을 복구했습니다. 다음 상품 바코드를 스캔하세요.`
            }
          } catch (err) {
            // Keep the recovery record; never send another print request here.
            if (Number(this.selectedOrder?.orderId) === Number(row.orderId)) {
              const previous = this.messageText
              const failure = this.expressFailure(err, 'FINALIZE')
              if (this.formatExpressFailure(failure).split(' / 문의번호:')[0] !== previous.split(' / 문의번호:')[0]) {
                this.showExpressFailure(failure, row.orderNo, 'EXPRESS_RECOVERY')
              }
            }
          }
        }
      } finally { this.isRetryingExpressFinalization = false }
      if (recovered) await this.loadRecentPackedOrders()
    },
    enqueueExpressScan(barcode, context = null) {
      const value = String(barcode || '').trim()
      if (!value) return
      if (!this.beginProductScan()) return
      const scan = { barcode: value, partnerId: context?.partnerId ?? this.selectedPartnerId,
        rangePreset: context?.rangePreset || this.lookupRangePreset, packingMode: 'EXPRESS',
        memberId: context?.memberId ?? String(this.session?.memberId ?? this.session?.id ?? '') }
      // A single accepted scan only. No later physical scan is retained or replayed.
      this.expressScanQueue = [scan]
      this.barcode = ''
      this.expressQueuePromise = this.drainExpressScans()
      return this.expressQueuePromise
    },
    async drainExpressScans() {
      if (this.isProcessingExpressQueue || this.pendingSkuResume || this.skuRecoveryActive || this.isResumingSku) return
      this.isProcessingExpressQueue = true
      const scan = this.expressScanQueue.shift()
      try {
        if (scan && !this.expressRecoveryStopped) await this.processExpressScan(scan)
      } catch (err) {
        this.showExpressFailure(err, scan?.barcode)
        this.playErrorSound()
      } finally {
        this.expressScanQueue = []
        this.isProcessingExpressQueue = false
        await this.finishProductScan()
      }
    },
    async processExpressScan(scan) {
      if (!this.isExpressScanContextCurrent(scan)) throw this.expressFailure(new Error('스캔 이후 작업 컨텍스트가 변경되었습니다.'), 'LOOKUP', 'CONTEXT_CHANGED')
      await this.retryPendingExpressFinalizations()
      this.lastScannedBarcode = scan.barcode
      let result
      for (let i = 0; i < 3; i += 1) {
        try { result = await this.autoSelectPacking(scan.barcode, scan); break }
        catch (err) {
          if (err?.response && err.response.status < 500 && ![408, 429].includes(err.response.status)) throw err
          if (i === 2) throw err
          await this.expressDelay(500 * (i + 1))
        }
      }
      if (!this.isExpressScanContextCurrent(scan)) throw this.expressFailure(new Error('조회 중 작업 컨텍스트가 변경되었습니다.'), 'LOOKUP', 'CONTEXT_CHANGED')
      if (!result?.selected) {
        const failure = this.expressFailure(new Error('처리 가능한 익스프레스 오더가 없습니다.'), 'LOOKUP', result?.code === 'NO_EXPRESS_ELIGIBLE_ORDER' ? 'NO_EXPRESS_ELIGIBLE_ORDER' : 'NO_PENDING_ORDER')
        failure.barcodeStatus = result?.barcodeStatus
        throw failure
      }
      const selected = result.selected
      const pending = this.readPendingExpressFinalizations().find(row => Number(row.orderId) === Number(selected.selectedOrder?.orderId))
      if (pending) {
        throw this.expressFailure(new Error('이전 출력건의 관리자 확인이 필요합니다.'), 'RECOVERY',
          this.isRecoverableExpressFinalization(pending) ? 'FINALIZE_PENDING' : 'PRINT_UNKNOWN')
      }
      this.selectedOrder = selected.selectedOrder
      this.packingItems = this.buildPackingItems(selected.items || [])
      this.draftMasterSkus = selected.draftMasterSkus || []
      this.pageState = PAGE_STATE.ORDER_SELECTED
      this.assertSingleExpressItem(this.packingItems)
      if (!await this.ensureSelectedOrderSkus('EXPRESS', scan)) return false
      if (this.draftMasterSkus.length) {
        this.messageType = 'warning'
        this.messageText = '가등록 상품의 스펙과 중량을 확정하면 자동 출력과 포장완료가 이어집니다.'
        this.addScanLog('DRAFT_SKU_CHECK', scan.barcode, 'WAIT', '가등록 상품 스펙·중량 확정 대기')
        const confirmed = await this.waitForExpressDraftConfirmation()
        if (!confirmed || this.expressRecoveryStopped) return false
        if (!this.isExpressMode || Number(scan.partnerId) !== Number(this.selectedPartnerId)
          || Number(this.selectedOrder?.orderId) !== Number(selected.selectedOrder?.orderId)) {
          throw new Error('가등록 상품 확인 중 포장 대상이 변경되었습니다. 다시 스캔하세요.')
        }
      }
      return this.completeExpressPacking({ selectedOrder: this.selectedOrder, items: this.packingItems })
    },
    waitForExpressDraftConfirmation() {
      return new Promise(resolve => {
        this.pendingExpressDraftConfirmation = { resolve }
        this.draftSkuConfirmOpen = true
      })
    },
    settleExpressDraftConfirmation(confirmed) {
      const pending = this.pendingExpressDraftConfirmation
      this.pendingExpressDraftConfirmation = null
      if (pending) pending.resolve(confirmed)
    },
    isSelectedPartnerOrder(order) {
      const orderPartnerId = Number(order?.partnerId)
      const selectedPartnerId = Number(this.selectedPartnerId)
      return Number.isFinite(orderPartnerId)
        && Number.isFinite(selectedPartnerId)
        && orderPartnerId > 0
        && selectedPartnerId > 0
        && orderPartnerId === selectedPartnerId
    },
    getPartnerMismatchMessage() {
      return "선택된 파트너와 오더의 파트너가 일치하지 않습니다. 다시 조회해 주세요."
    },
    openBackupCompletion() {
      if (!this.canCompleteBackup) return
      const order = this.selectedOrder
      this.backupCompletionTarget = {
        orderId: Number(order.orderId), partnerId: Number(this.selectedPartnerId),
        packingResetId: String(order.packingResetId || "0"),
        orderNo: order.orderNo, hblNo: order.hblNo
      }
      const pending = this.readPendingExpressFinalizations().find(row => Number(row.orderId) === Number(order.orderId))
      this.backupCompletionWeight = String(this.finalWeightInput || pending?.printedWeight || order.weight || "")
      this.backupCompletionError = ""
      this.backupCompletionOpen = true
    },
    cancelBackupCompletion() {
      if (this.isSubmittingBackupCompletion) return
      this.backupCompletionOpen = false
      this.backupCompletionTarget = null
      this.focusActiveInput()
    },
    async submitBackupCompletion() {
      if (!this.backupCompletionOpen || this.isSubmittingBackupCompletion || !this.canCompleteBackup) return
      const target = this.backupCompletionTarget
      if (!target || target.orderId !== Number(this.selectedOrder?.orderId)
        || target.partnerId !== Number(this.selectedPartnerId)) {
        this.backupCompletionError = "선택된 오더 또는 파트너가 변경되었습니다. 다시 조회하세요."
        return
      }
      const weight = this.normalizedBackupCompletionWeight
      if (weight === null) {
        this.backupCompletionError = "최종 중량을 0.01~9999.99kg, 소수점 둘째 자리까지 입력하세요."
        return
      }
      this.isSubmittingBackupCompletion = true
      this.backupCompletionError = ""
      try {
        const res = await this.$http({
          method: "POST", url: "/api/fulfillment/packing/manual-complete", timeout: 15000,
          data: { id: target.orderId, partnerId: target.partnerId, packingResetId: target.packingResetId, weight, confirmed: true,
            mode: this.isExpressMode ? "EXPRESS" : "NORMAL" },
          name: "[ AXIOS / 포장 / 수동 포장완료 및 임시콘솔 ]"
        })
        const result = res?.data?.data
        if (res?.data?.ok !== true || result?.state?.packingStatus !== "COMPLETED"
          || result.state.tempConsolStatus !== "READY" || Number(result.weight) !== weight) {
          throw new Error(res?.data?.message || "수동 완료 저장 결과를 확인할 수 없습니다.")
        }
        this.removePendingExpressFinalization(target.orderId)
        this.selectedOrder = { ...this.selectedOrder, weight: result.weight, packingCompleted: true,
          packingStatus: "COMPLETED", tempConsolStatus: "READY", waybillPrinted: result.state.waybillPrinted }
        this.recordRecentPackedOrder(this.selectedOrder, this.packingItems)
        await this.loadRecentPackedOrders()
        this.pageState = PAGE_STATE.COMPLETED
        this.backupCompletionOpen = false
        this.backupCompletionTarget = null
        this.messageType = "positive"
        this.messageText = `${target.orderNo} 오더를 수동으로 포장완료하고 임시콘솔목록에 반영했습니다.`
        this.addScanLog("PACKING_MANUAL_COMPLETE", target.orderNo, "SUCCESS", `최종 중량 ${weight}kg / 임시콘솔 대기`)
        this.playSuccessSound()
        this.completionActionModalOpen = true
      } catch (err) {
        this.backupCompletionError = err?.response?.data?.message || err?.message || "수동 완료 저장에 실패했습니다. 다시 시도하세요."
        this.addScanLog("PACKING_MANUAL_COMPLETE", target.orderNo, "FAIL", this.backupCompletionError)
        this.playErrorSound()
      } finally {
        this.isSubmittingBackupCompletion = false
      }
    },
    async completePackingOrder(orderId, { expressMode = false, weight = null } = {}) {
      const config = {
        method: "POST",
        url: "/api/fulfillment/packing/complete",
        data: {
          id: orderId,
          packingResetId: String(this.selectedOrder?.packingResetId || "0"),
          status: "03",
          partnerId: this.selectedPartnerId,
          expressMode,
          ...(expressMode ? {} : { weight })
        },
        name: "[ AXIOS / 포장 / 포장완료 ]"
      }

      const res = await this.$http(config)
      if (res?.data?.ok === false) throw new Error(res?.data?.message || "packing complete API failed")
      return res?.data?.data || {}
    },
    async recordPackingProgress(eventType, data = {}) {
      const itemScan = eventType === 'ITEM_SCAN'
      if (itemScan) {
        this.productScanUncertain = { orderId: this.selectedOrder?.orderId, orderNo: this.selectedOrder?.orderNo,
          partnerId: this.selectedPartnerId, packingResetId: String(this.selectedOrder?.packingResetId || '0'),
          orderItemId: data.orderItemId, barcode: data.barcode }
        try { this.persistProductScanState() }
        catch (_) {
          this.productScanUncertain = null
          throw new Error('복구 기록을 저장할 수 없어 상품 수량을 전송하지 않았습니다. 브라우저 저장소를 확인하세요.')
        }
      }
      try {
        const res = await this.$http({
          method: "POST",
          url: "/api/fulfillment/packing/progress",
          data: {
            id: this.selectedOrder?.orderId,
            packingResetId: String(this.selectedOrder?.packingResetId || "0"),
            partnerId: this.selectedPartnerId,
            eventType,
            ...data
          },
          name: "[ AXIOS / 포장 / 진행기록 ]"
        })
        if (res?.data?.ok === false) throw Object.assign(new Error(res.data.message || 'packing progress API failed'), { response: res })
        const scannedItem = itemScan ? this.packingItems.find(item => Number(item.id) === Number(data.orderItemId)) : null
        if (itemScan && (res?.data?.ok !== true || res.data.data?.recorded !== true
          || Number(res.data.data.orderId) !== Number(this.selectedOrder?.orderId)
          || Number(res.data.data.orderItemId) !== Number(data.orderItemId)
          || res.data.data.pickedQty == null || !Number.isInteger(Number(res.data.data.pickedQty))
          || !scannedItem || Number(res.data.data.pickedQty) !== Number(scannedItem.pickedQty) + 1
          || Number(res.data.data.pickedQty) > Number(scannedItem.requiredQty))) {
          throw new Error('상품 수량 저장 응답을 확인할 수 없습니다.')
        }
        if (itemScan) {
          const pending = this.productScanUncertain
          this.productScanUncertain = null
          try { this.persistProductScanState() }
          catch (err) { this.productScanUncertain = pending; throw err }
        }
        return res?.data?.data || {}
      } catch (err) {
        // A structured business rejection confirms no write; transport/5xx/malformed responses do not.
        if (itemScan && [400, 401, 403, 404, 409, 422].includes(err?.response?.status)
          && err?.response?.data?.ok === false) {
          const pending = this.productScanUncertain
          this.productScanUncertain = null
          try { this.persistProductScanState() }
          catch (storageErr) { this.productScanUncertain = pending; throw storageErr }
        }
        throw err
      }
    },
    getPackedOrderSnapshot(order, items = []) {
      const itemNames = items
        .map((item) => item.name || item.itemName)
        .filter(Boolean)
        .join(", ")

      return {
        orderId: order?.orderId || null,
        orderNo: order?.orderNo || "-",
        hblNo: order?.hblNo || "-",
        itemNames: itemNames || "-",
        partnerId: order?.partnerId || this.selectedPartnerId || null,
        partnerName: this.selectedPartnerLabel || "-",
        packingStatus: "COMPLETED",
        tempConsolStatus: "READY"
      }
    },
    recordRecentPackedOrder(order, items = []) {
      if (!order?.orderNo) return
      this.$store.dispatch("addRecentPackedOrder", this.getPackedOrderSnapshot(order, items))
    },
    normalizeRecentPackedRows(rows = []) {
      if (!Array.isArray(rows)) return []
      return rows.map((row) => ({
        orderId: row.orderId || null,
        orderNo: row.orderNo || "-",
        hblNo: row.hblNo || "-",
        itemNames: row.itemNames || "-",
        partnerId: row.partnerId || null,
        partnerName: row.partnerName || "-",
        packingStatus: row.packingStatus || "COMPLETED",
        tempConsolStatus: row.tempConsolStatus || "READY",
        packingCompletedAt: row.packingCompletedAt || "",
        tempConsolReadyAt: row.tempConsolReadyAt || "",
        totalQty: Number(row.totalQty || 0),
        totalWeight: Number(row.totalWeight || 0)
      }))
    },
    async loadRecentPackedOrders() {
      if (this.recentPackedLoading) return

      try {
        this.recentPackedLoading = true
        const res = await this.$http({
          method: "POST",
          url: "/api/fulfillment/packing/recent-completed",
          data: {
            partnerId: this.selectedPartnerId || "",
            hours: 24,
            limit: 30
          },
          name: "[ AXIOS / 포장 / 최근 포장완료 조회 ]"
        })
        const rows = this.normalizeRecentPackedRows(res?.data?.data?.rows || [])
        this.$store.dispatch("setRecentPackedOrders", rows)
      } catch (err) {
        this.$log?.warn?.("[ Packing / 최근 포장완료 조회 실패 ]", err)
      } finally {
        this.recentPackedLoading = false
      }
    },
    goParcelInfoWithRecentPacked() {
      this.$router.push({
        path: "/admin/parcel-info",
        query: { from: "packing", focus: "recent-packed" }
      })
    },
    isSingleExpressItem(items) {
      return Array.isArray(items) && items.length === 1 && Number(items[0].requiredQty) === 1
    },
    assertSingleExpressItem(items) {
      if (!this.isSingleExpressItem(items)) {
        throw this.expressFailure(new Error('상품 한 행, 수량 1개인 주문만 익스프레스로 처리할 수 있습니다.'), 'LOOKUP', 'PACKING_EXPRESS_SINGLE_ITEM_REQUIRED')
      }
    },
    async completeExpressPacking(selected) {
      const order = selected?.selectedOrder
      if (!order?.orderId) throw new Error("익스프레스 완료 대상 오더가 없습니다.")
      if (!this.isSelectedPartnerOrder(order)) throw new Error(this.getPartnerMismatchMessage())
      if (this.activeExpressOrderId) throw new Error('다른 익스프레스 오더를 처리 중입니다.')
      this.activeExpressOrderId = order.orderId
      try { return await this.completeDirectExpressPacking(selected) }
      finally { this.activeExpressOrderId = null }
    },
    async completeDirectExpressPacking(selected) {
      const order = selected.selectedOrder
      let record = this.readPendingExpressFinalizations().find(row => Number(row.orderId) === Number(order.orderId))
      let printed = false
      if (record && !this.isRecoverableExpressFinalization(record)) {
        throw this.expressFailure(new Error('이전 출력 접수 여부를 확인할 수 없습니다. 재인쇄하지 말고 관리자에게 문의하세요.'), 'RECOVERY', 'PRINT_UNKNOWN')
      }
      if (!record) {
        this.assertSingleExpressItem(selected.items || this.packingItems)
        const result = await this.printExpressWaybillForOrder(order)
        if (!result || !Number.isFinite(Number(result.weight)) || Number(result.weight) <= 0) {
          throw new Error('출력 무게를 확인할 수 없습니다.')
        }
        printed = result.printed
        record = { ...order, stage: 'FINALIZE_PENDING', printedWeight: Number(result.weight) }
        this.queuePendingExpressFinalization(record)
      }
      try { await this.requestExpressFinalization(record) }
      catch (err) {
        const failure = new Error('인쇄는 접수됐으며 같은 오더의 완료만 자동 재시도합니다.')
        failure.response = err?.response
        failure.cause = err
        throw this.expressFailure(failure, 'FINALIZE', err?.expressCode)
      }
      this.removePendingExpressFinalization(order.orderId)

      const expressItems = selected?.items || []
      const scannedItem = expressItems.find((item) => item.barcode === this.lastScannedBarcode) || expressItems[0] || {
        name: order.itemNames || order.itemName || order.orderNo,
        barcode: this.lastScannedBarcode,
        pickedQty: 1,
        requiredQty: 1
      }
      this.recordSuccessfulItemScan({
        ...scannedItem,
        pickedQty: Number(scannedItem.pickedQty || 1) || 1,
        requiredQty: Number(scannedItem.requiredQty || 1) || 1
      })
      this.recordRecentPackedOrder(order, expressItems)
      this.loadRecentPackedOrders().catch(err => this.$log?.warn?.('[ Packing / 최근 완료 조회 실패 ]', err))
      this.pageState = PAGE_STATE.IDLE
      this.messageType = "positive"
      this.messageText = `${order.orderNo} 오더의 운송장 출력 요청이 접수되고 포장완료가 저장되어 임시콘솔목록에 추가되었습니다. 다음 상품 바코드를 스캔하세요.`
      this.addScanLog(
        "EXPRESS_COMPLETE",
        order.orderNo,
        "SUCCESS",
        printed ? "익스프레스 출력/완료" : "기존 출력건 최종화 복구"
      )
      this.playSuccessSound()
      this.selectedOrder = null
      this.packingItems = []
      this.waybillPrintRequested = false
      // Do not erase a partially typed next physical scan during asynchronous completion.
      this.hblStartInput = ""
      this.itemBarcodeInput = ""
      this.hblCompleteInput = ""
      this.finalWeightInput = ""
      if (!this.scanMilestoneConfirmOpen) this.focusActiveInput(true)
    },
    /**
     * Title        : 스캔 로그 기록
     * Status       : Actived
     * Description  : 최근 스캔 로그를 최대 10건까지 유지한다.
     * Param (type) : 로그 유형
     * Param (inputValue) : 입력값
     * Param (result) : 성공/실패 결과
     * Param (detail) : 상세 메시지
     */
    addScanLog(type, inputValue, result, detail = "") {
      const stamp = new Date().toLocaleTimeString("ko-KR", { hour12: false })
      this.scanLogs.unshift({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        at: stamp,
        type,
        input: inputValue || "-",
        result,
        detail
      })
      this.scanLogs = this.scanLogs.slice(0, 10)
    },
    openScanMilestoneConfirm(item) {
      this.scanMilestoneItem = {
        name: item?.name || "-",
        barcode: item?.barcode || "-",
        pickedQty: Number(item?.pickedQty || 0),
        requiredQty: Number(item?.requiredQty || 0),
        count: this.successfulItemScanCount
      }
      this.scanMilestoneConfirmOpen = true
    },
    recordSuccessfulItemScan(item) {
      this.successfulItemScanCount += 1
      if (!this.isExpressMode && this.successfulItemScanCount > 0 && this.successfulItemScanCount % 10 === 0) {
        this.openScanMilestoneConfirm(item)
      }
    },
    confirmScanMilestone() {
      this.scanMilestoneConfirmOpen = false
      this.scanMilestoneItem = null
      this.focusActiveInput()
    },
    calculateDraftFlightWeight(item) {
      const form = item?.specForm || {}
      const length = Number(form.length_cm)
      const width = Number(form.width_cm)
      const height = Number(form.height_cm)
      if (!Number.isFinite(length) || !Number.isFinite(width) || !Number.isFinite(height)) return ""
      if (length <= 0 || width <= 0 || height <= 0) return ""
      const result = (length * width * height) / 6000
      return Math.round(result * 100) / 100
    },
    applyDraftFlightWeight(item) {
      const flightWeight = this.calculateDraftFlightWeight(item)
      if (flightWeight === "") {
        this.messageType = "negative"
        this.messageText = `${item?.barcode || "-"} 가등록 상품의 가로, 세로, 높이를 먼저 입력하세요.`
        this.playErrorSound()
        return
      }
      item.specForm.weight_kg = flightWeight
    },
    /**
     * Title        : 상태별 입력 포커스 이동
     * Status       : Actived
     * Description  : 현재 상태에 맞는 input으로 포커스를 이동한다.
     * Param (force): 모달 열림 상태에서도 강제 포커스 여부
     */
    focusActiveInput(force = false) {
      if (this.skuRecoveryActive) return
      if (!force && this.isBlockingDialogOpen) return
      this.$nextTick(() => {
        const refName = this.activeInputRef
        if (!refName) return

        const targetRef = this.$refs[refName]
        if (!targetRef) {
          // 피킹 패널 분리 후 입력 ref는 자식 컴포넌트 내부에 존재한다.
          const pickingPanelRef = this.$refs.pickingListPanelRef
          const nestedInPicking = pickingPanelRef?.$refs?.[refName]
          if (nestedInPicking && typeof nestedInPicking.focus === "function") {
            nestedInPicking.focus()
            return
          }
          return
        }

        if (typeof targetRef.focus === "function") {
          targetRef.focus()
          return
        }

        // 분리 컴포넌트 내부에 동일 refName 또는 입력 ref가 있으면 우선 포커스한다.
        const nestedSameRef = targetRef.$refs?.[refName]
        if (nestedSameRef && typeof nestedSameRef.focus === "function") {
          nestedSameRef.focus()
          return
        }
        const nestedBarcodeInput = targetRef.$refs?.barcodeInput
        if (nestedBarcodeInput && typeof nestedBarcodeInput.focus === "function") {
          nestedBarcodeInput.focus()
          return
        }

        // q-btn 같은 컴포넌트 ref는 focus 메서드가 없을 수 있어 실제 focus 가능한 DOM을 찾아 처리한다.
        const el = targetRef.$el || targetRef
        const focusable = el?.querySelector?.("button, input, textarea, [tabindex]")
        if (focusable && typeof focusable.focus === "function") {
          focusable.focus()
        }
      })
    },
    /**
     * Title        : 전역 클릭 포커스 복구
     * Status       : Actived
     * Description  : 화면 클릭 시 활성 입력창으로 포커스를 복구한다.
     */
    handleGlobalClick() {
      this.focusActiveInput()
    },
    /**
     * Title        : 전역 키다운 처리
     * Status       : Actived
     * Description  : 단축키(`/`, `Escape`)로 입력 포커스를 복구한다.
     * Param (evt)  : keyboard event
     */
    handleGlobalKeydown(evt) {
      if (this.skuRecoveryActive) return
      if (this.isComposing) return
      if (evt.key === "/" || evt.key === "Escape") {
        evt.preventDefault()
        this.focusActiveInput()
      }
    },
    /**
     * Title        : 탭 복귀 시 포커스 복구
     * Status       : Actived
     * Description  : 브라우저 탭 활성화 시 입력 포커스를 재설정한다.
     */
    handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        this.focusActiveInput()
      }
    },
    /**
     * Title        : 시드니 현재시각 갱신
     * Status       : Actived
     * Description  : 운영 기준 시간대(`Australia/Sydney`) 현재 시각을 표시한다.
     */
    tickSydneyTime() {
      this.now = new Date().toLocaleString("ko-KR", {
        timeZone: "Australia/Sydney",
        hour12: false
      })
    },
    /**
     * Title        : 오디오 컨텍스트 활성화
     * Status       : Actived
     * Description  : 브라우저 정책에 맞춰 첫 사용자 제스처에서 오디오 컨텍스트를 활성화한다.
     */
    async unlockAudio() {
      const AudioCtx = window.AudioContext || window.webkitAudioContext
      if (!AudioCtx) return

      if (!this.audioCtx) {
        this.audioCtx = new AudioCtx()
      }

      if (this.audioCtx.state === "suspended") {
        await this.audioCtx.resume()
      }
      this.audioUnlocked = this.audioCtx.state === "running"

      if (!this.audioUnlocked) return
      await this.ensureAudioWorklet()
    },
    /**
     * Title        : AudioWorklet 엔진 준비
     * Status       : Actived
     * Description  : 소리 패턴 재생용 AudioWorklet 노드를 초기화한다.
     */
    async ensureAudioWorklet() {
      if (!this.audioCtx || this.audioWorkletReady) return
      if (!this.audioCtx.audioWorklet) return

      try {
        if (!this.audioWorkletModuleUrl) {
          const processorSource = `
class PackingChimeProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.voices = [];
    this.sampleCursor = 0;
    this.port.onmessage = (evt) => {
      const data = evt.data || {};
      if (data.type !== 'play-pattern' || !Array.isArray(data.tones)) return;
      for (const tone of data.tones) {
        const durationMs = Math.max(20, Number(tone.durationMs || 100));
        const offsetMs = Math.max(0, Number(tone.offsetMs || 0));
        const frequency = Math.max(40, Number(tone.frequency || 440));
        const volume = Math.max(0.001, Math.min(0.2, Number(tone.volume || 0.05)));
        const wave = String(tone.wave || 'sine');
        this.voices.push({
          startSample: this.sampleCursor + Math.floor((offsetMs / 1000) * sampleRate),
          totalSamples: Math.floor((durationMs / 1000) * sampleRate),
          remaining: Math.floor((durationMs / 1000) * sampleRate),
          phase: 0,
          phaseInc: (2 * Math.PI * frequency) / sampleRate,
          volume,
          wave
        });
      }
    };
  }

  shape(wave, phase) {
    if (wave === 'square') return Math.sign(Math.sin(phase)) || 1;
    if (wave === 'triangle') return (2 / Math.PI) * Math.asin(Math.sin(phase));
    if (wave === 'sawtooth') return 2 * (phase / (2 * Math.PI) - Math.floor(phase / (2 * Math.PI) + 0.5));
    return Math.sin(phase);
  }

  process(_inputs, outputs) {
    const output = outputs[0];
    if (!output || output.length === 0) return true;
    const ch0 = output[0];
    ch0.fill(0);

    for (let i = 0; i < ch0.length; i++) {
      let sample = 0;
      const nowSample = this.sampleCursor + i;
      const nextVoices = [];

      for (const voice of this.voices) {
        if (nowSample < voice.startSample) {
          nextVoices.push(voice);
          continue;
        }

        if (voice.remaining <= 0) continue;

        const progressed = voice.totalSamples - voice.remaining;
        const attack = Math.max(1, Math.floor(voice.totalSamples * 0.08));
        const release = Math.max(1, Math.floor(voice.totalSamples * 0.18));
        let env = 1;
        if (progressed < attack) env = progressed / attack;
        else if (voice.remaining < release) env = voice.remaining / release;

        sample += this.shape(voice.wave, voice.phase) * voice.volume * env;
        voice.phase += voice.phaseInc;
        if (voice.phase > Math.PI * 2) voice.phase -= Math.PI * 2;
        voice.remaining -= 1;
        if (voice.remaining > 0) nextVoices.push(voice);
      }

      this.voices = nextVoices;
      if (sample > 1) sample = 1;
      if (sample < -1) sample = -1;
      ch0[i] = sample;
    }

    for (let c = 1; c < output.length; c++) {
      output[c].set(ch0);
    }
    this.sampleCursor += ch0.length;
    return true;
  }
}
registerProcessor('packing-chime-processor', PackingChimeProcessor);
          `
          const blob = new Blob([processorSource], { type: "text/javascript" })
          this.audioWorkletModuleUrl = URL.createObjectURL(blob)
        }

        await this.audioCtx.audioWorklet.addModule(this.audioWorkletModuleUrl)
        this.audioWorkletNode = new AudioWorkletNode(this.audioCtx, "packing-chime-processor")
        this.audioWorkletGain = this.audioCtx.createGain()
        this.audioWorkletGain.gain.value = 0.9
        this.audioWorkletNode.connect(this.audioWorkletGain)
        this.audioWorkletGain.connect(this.audioCtx.destination)
        this.audioWorkletReady = true
      } catch (err) {
        this.$log?.warn?.("[ Packing / AudioWorklet init 실패 ]", err)
        this.audioWorkletReady = false
      }
    },
    /**
     * Title        : 최초 사용자 제스처 처리
     * Status       : Actived
     * Description  : 클릭/키입력 시 오디오를 초기화하고 리스너를 해제한다.
     */
    async handleFirstUserGesture() {
      try {
        await this.unlockAudio()
      } catch (_) {
        // noop
      }
      if (this.audioUnlocked) {
        window.removeEventListener("pointerdown", this.handleFirstUserGesture)
        window.removeEventListener("keydown", this.handleFirstUserGesture)
      }
    },
    /**
     * Title        : 비프음 재생
     * Status       : Actived
     * Description  : 지정 주파수/길이로 단일 비프음을 재생한다.
     */
    async playBeepFallback({ frequency = 880, durationMs = 80, volume = 0.05, type = "sine" } = {}) {
      try {
        await this.unlockAudio()
      } catch (_) {
        return
      }
      if (!this.audioCtx || !this.audioUnlocked) return

      const now = this.audioCtx.currentTime
      const osc = this.audioCtx.createOscillator()
      const gain = this.audioCtx.createGain()

      osc.type = type
      osc.frequency.setValueAtTime(frequency, now)

      gain.gain.setValueAtTime(0.0001, now)
      gain.gain.exponentialRampToValueAtTime(volume, now + 0.01)
      gain.gain.exponentialRampToValueAtTime(0.0001, now + durationMs / 1000)

      osc.connect(gain)
      gain.connect(this.audioCtx.destination)
      osc.start(now)
      osc.stop(now + durationMs / 1000 + 0.02)
    },
    /**
     * Title        : 사운드 패턴 재생
     * Status       : Actived
     * Description  : AudioWorklet 우선, 미지원 시 fallback 방식으로 패턴음을 재생한다.
     */
    async playPattern(tones = []) {
      try {
        await this.unlockAudio()
      } catch (_) {
        return
      }
      if (!tones.length) return

      if (this.audioWorkletReady && this.audioWorkletNode) {
        this.audioWorkletNode.port.postMessage({ type: "play-pattern", tones })
        return
      }

      tones.forEach((tone) => {
        const offsetMs = Number(tone.offsetMs || 0)
        window.setTimeout(() => {
          this.playBeepFallback({
            frequency: tone.frequency,
            durationMs: tone.durationMs,
            volume: tone.volume,
            type: tone.wave || "sine"
          })
        }, offsetMs)
      })
    },
    /**
     * Title        : 스캔 성공음
     * Status       : Actived
     * Description  : 상품 스캔 성공 시 '띵동' 느낌의 2음 차임을 재생한다.
     */
    playSuccessSound() {
      this.playPattern([
        { frequency: 1319, durationMs: 120, volume: 0.05, wave: "triangle", offsetMs: 0 },
        { frequency: 988, durationMs: 180, volume: 0.055, wave: "triangle", offsetMs: 130 }
      ])
    },
    /**
     * Title        : 스캔 실패음
     * Status       : Actived
     * Description  : 상품 스캔 실패 시 경고 성향이 강한 비프음을 재생한다.
     */
    playErrorSound() {
      this.playPattern([
        { frequency: 330, durationMs: 110, volume: 0.08, wave: "square", offsetMs: 0 },
        { frequency: 330, durationMs: 110, volume: 0.08, wave: "square", offsetMs: 140 },
        { frequency: 330, durationMs: 110, volume: 0.08, wave: "square", offsetMs: 280 }
      ])
    },
    /**
     * Title        : 자동선택용 목업 아이템 생성
     * Status       : Actived
     * Description  : API 연동 전 피킹 리스트 목업 데이터를 반환한다.
     */
    createMockItems() {
      return [
        { id: 1, name: "Kitchen Towel", barcode: "KT-001", requiredQty: 2, pickedQty: 0, status: "미완료" },
        { id: 2, name: "Storage Box", barcode: "SB-010", requiredQty: 1, pickedQty: 0, status: "미완료" },
        { id: 3, name: "Bath Mat", barcode: "BM-777", requiredQty: 3, pickedQty: 0, status: "미완료" },
      ]
    },
    /**
     * Title        : 수동조회 목업 오더 생성
     * Status       : Actived
     * Description  : API 실패 시 사용하는 수동조회 목업 목록을 반환한다.
     */
    createManualLookupRows() {
      return [
        {
          orderId: 13101,
          orderNo: "ORD-20260304-011",
          hblNo: "HBL-99001991",
          customerName: "AONE CUSTOMER A",
          destination: "SYDNEY",
          createdAt: "2026-03-04 08:15:22",
          firstBarcode: "8801234567890",
          selectionReason: "수동조회 선택",
          items: [
            { id: 101, name: "Kitchen Towel", barcode: "KT-001", requiredQty: 2, pickedQty: 0 },
            { id: 102, name: "Storage Box", barcode: "SB-010", requiredQty: 1, pickedQty: 0 },
          ]
        },
        {
          orderId: 13102,
          orderNo: "ORD-20260304-012",
          hblNo: "HBL-99001992",
          customerName: "AONE CUSTOMER B",
          destination: "MELBOURNE",
          createdAt: "2026-03-04 08:22:40",
          firstBarcode: "8805555123456",
          selectionReason: "수동조회 선택",
          items: [
            { id: 201, name: "Bath Mat", barcode: "BM-777", requiredQty: 3, pickedQty: 0 },
            { id: 202, name: "Laundry Basket", barcode: "LB-321", requiredQty: 1, pickedQty: 0 },
          ]
        },
        {
          orderId: 13103,
          orderNo: "ORD-20260304-013",
          hblNo: "HBL-99001993",
          customerName: "AONE CUSTOMER C",
          destination: "BRISBANE",
          createdAt: "2026-03-04 08:31:03",
          firstBarcode: "8809999000011",
          selectionReason: "수동조회 선택",
          items: [
            { id: 301, name: "Paper Towel", barcode: "PT-889", requiredQty: 4, pickedQty: 0 },
            { id: 302, name: "Dish Rack", barcode: "DR-441", requiredQty: 1, pickedQty: 0 },
          ]
        }
      ]
    },
    /**
     * Title        : 수동조회 검색 실행
     * Status       : Actived
     * Description  : 수동조회 API를 조회하고 실패 시 오류 상태를 표시한다.
     */
    async searchManualLookup() {
      if (this.partnerSelectionRequired) {
        this.manualLookupRows = []
        this.messageType = "warning"
        this.messageText = "수동조회 전에 파트너를 먼저 선택하세요."
        return
      }

      this.manualLookupLoading = true
      try {
        const rows = await this.fetchManualLookupRows(this.manualLookupKeyword)
        this.manualLookupRows = rows
      } catch (err) {
        this.$log?.warn?.("[ Packing / 수동조회 API 실패 ]", err)
        this.manualLookupRows = []
        this.messageType = "negative"
        this.messageText = "수동조회 API 조회에 실패했습니다. 서버 연결 상태를 확인해 주세요."
      } finally {
        this.manualLookupLoading = false
      }
    },
    /**
     * Title        : 아이템 상태 갱신
     * Status       : Actived
     * Description  : 담은수량 기준으로 `미완료/진행중/완료` 상태를 계산한다.
     * Param (item) : 피킹 아이템
     */
    updateItemStatus(item) {
      if (item.pickedQty <= 0) item.status = "미완료"
      else if (item.pickedQty < item.requiredQty) item.status = "진행중"
      else item.status = "완료"
    },
    /**
     * Title        : 피킹 아이템 모델 빌드
     * Status       : Actived
     * Description  : 원본 아이템 목록을 화면용 포장 아이템 모델로 변환한다.
     * Param (items): 원본 아이템 목록
     */
    buildPackingItems(items = []) {
      return items.map((item) => {
        const normalized = {
          id: item.id,
          name: item.name,
          barcode: item.barcode,
          requiredQty: item.requiredQty,
          pickedQty: item.pickedQty || 0,
          status: "미완료"
        }
        this.updateItemStatus(normalized)
        return normalized
      })
    },
    restorePersistedWorkflowState() {
      const workflowState = this.selectedOrder?.workflowState
      if (!workflowState?.started) return false

      this.successfulItemScanCount = this.packingItems.reduce(
        (sum, item) => sum + Number(item.pickedQty || 0),
        0
      )
      if (workflowState.finalHblConfirmed) {
        this.pageState = PAGE_STATE.WEIGHT_REQUIRED
        this.messageType = "warning"
        this.messageText = "최종 HBL 확인이 복원되었습니다. 실측 무게를 입력하세요."
      } else if (this.isAllItemsCompleted) {
        this.pageState = PAGE_STATE.READY_TO_COMPLETE
        this.messageType = "warning"
        this.messageText = "상품 스캔 수량이 복원되었습니다. 최종 HBL을 스캔하세요."
      } else {
        this.pageState = PAGE_STATE.PACKING_STARTED
        this.messageType = "info"
        this.messageText = "진행 중인 포장 수량을 복원했습니다. 남은 상품을 스캔하세요."
      }
      return true
    },
    /**
     * Title        : 자동 오더 선택 실행
     * Status       : Actived
     * Description  : 스캔된 바코드로 API 자동선택을 수행하고 실패 시 오류 상태를 표시한다.
     */
    async simulateAutoSelect() {
      if (this.isComposing) return
      if (this.isExpressMode) return this.enqueueExpressScan(this.barcode)
      if (!this.beginProductScan()) return
      this.isSelectingNormalOrder = true
      try { return await this.selectNormalOrder() }
      finally { this.isSelectingNormalOrder = false; await this.finishProductScan() }
    },
    async selectNormalOrder() {
      if (this.backupCompletionOpen || this.isSubmittingBackupCompletion) return
      if (this.scanMilestoneConfirmOpen) {
        this.barcode = ""
        return
      }

      if (this.partnerSelectionRequired) {
        this.pageState = PAGE_STATE.ERROR
        this.messageType = "warning"
        this.messageText = "바코드 스캔 전에 파트너를 먼저 선택하세요."
        this.addScanLog("BARCODE_SCAN", "", "FAIL", "파트너 미선택")
        this.playErrorSound()
        this.focusActiveInput()
        return
      }

      if (!this.barcode || !this.barcode.trim()) {
        this.pageState = PAGE_STATE.ERROR
        this.messageType = "negative"
        this.messageText = "바코드를 입력하거나 스캔해 주세요."
        this.addScanLog("BARCODE_SCAN", "", "FAIL", "빈 바코드")
        this.playErrorSound()
        this.focusActiveInput()
        return
      }

      if (this.isExpressMode) return this.enqueueExpressScan(this.barcode.trim())

      const scannedBarcode = this.barcode.trim()
      this.barcode = ""
      await this.retryPendingExpressFinalizations()
      this.lastScannedBarcode = scannedBarcode
      const lookupContext = { partnerId: this.selectedPartnerId, rangePreset: this.lookupRangePreset }
      let autoSelectResult = null
      let apiFailed = false
      try {
        autoSelectResult = await this.autoSelectPacking(this.lastScannedBarcode)
      } catch (err) {
        apiFailed = true
        this.$log?.warn?.("[ Packing / 자동선택 API 실패 ]", err)
      }

      const selected = autoSelectResult?.selected || null
      if (String(lookupContext.partnerId) !== String(this.selectedPartnerId) || lookupContext.rangePreset !== this.lookupRangePreset) {
        this.messageType = 'warning'
        this.messageText = '조회 중 파트너 또는 조회 기간이 변경되었습니다. 현재 조건에서 다시 스캔하세요.'
        this.focusActiveInput()
        return
      }

      if (selected) {
        this.selectedOrder = selected.selectedOrder
        this.packingItems = this.buildPackingItems(selected.items)
        this.draftMasterSkus = selected.draftMasterSkus || []
        this.pageState = PAGE_STATE.ORDER_SELECTED
        this.addScanLog("BARCODE_SCAN", this.lastScannedBarcode, "SUCCESS", this.selectedOrder.orderNo)

        if (this.selectedOrder.packingCompleted) {
          this.waybillPrintRequested = true
          this.messageType = "negative"
          this.messageText = `${this.selectedOrder.orderNo} 오더는 이미 운송장 출력과 포장이 완료된 건입니다. 자동 출력하지 않습니다.`
          this.addScanLog("WAYBILL_PRINT", this.selectedOrder.orderNo, "SKIP", "출력 완료 / 포장 완료")
          this.playErrorSound()
          this.barcode = ""
          this.hblStartInput = ""
          this.itemBarcodeInput = ""
          this.hblCompleteInput = ""
          this.focusActiveInput()
          return
        }

        if (!await this.ensureSelectedOrderSkus('SELECTION')) return

        if (this.draftMasterSkus.length > 0) {
          this.messageType = "warning"
          this.messageText = "가등록 상태의 상품이 포함되어 있습니다. 현장 확인 후 계속 진행하세요."
          this.addScanLog("DRAFT_SKU_CHECK", this.lastScannedBarcode, "WAIT", `${this.draftMasterSkus.length}개 가등록 상품 확인 필요`)
          this.playErrorSound()
          this.draftSkuConfirmOpen = true
          this.barcode = ""
          this.hblStartInput = ""
          this.itemBarcodeInput = ""
          this.hblCompleteInput = ""
          return
        }

        if (this.isExpressMode) {
          try {
            await this.completeExpressPacking(selected)
          } catch (err) {
            this.pageState = PAGE_STATE.ERROR
            this.messageType = "negative"
            this.messageText = err?.response?.data?.message || err?.message || "익스프레스 포장 처리에 실패했습니다."
            this.addScanLog("EXPRESS_COMPLETE", this.lastScannedBarcode, "FAIL", this.messageText)
            this.playErrorSound()
            this.barcode = ""
            this.hblStartInput = ""
            this.itemBarcodeInput = ""
            this.hblCompleteInput = ""
            this.focusActiveInput(true)
          }
          return
        }

        await this.continueAfterDraftSkuConfirm()
      } else if (!apiFailed) {
        this.pageState = PAGE_STATE.IDLE
        this.selectedOrder = null
        this.packingItems = []
        this.draftMasterSkus = []
        this.messageType = "negative"
        this.messageText = this.noOrderBarcodeNotice(autoSelectResult?.barcodeStatus)
        this.addScanLog("BARCODE_SCAN", this.lastScannedBarcode, "FAIL", this.messageText)
        this.playErrorSound()
      } else {
        this.pageState = PAGE_STATE.ERROR
        this.messageType = "negative"
        this.messageText = "자동선택 API 조회에 실패했습니다. 서버 연결 상태를 확인해 주세요."
        this.addScanLog("BARCODE_SCAN", this.lastScannedBarcode, "FAIL", "자동선택 API 실패")
        this.playErrorSound()
      }

      this.barcode = ""
      this.hblStartInput = ""
      this.itemBarcodeInput = ""
      this.hblCompleteInput = ""
      this.focusActiveInput()
    },
    async continueAfterDraftSkuConfirm() {
      this.draftSkuConfirmOpen = false
      if (this.pendingExpressDraftConfirmation) {
        this.settleExpressDraftConfirmation(true)
        return
      }
      if (!this.selectedOrder) return

      if (this.draftMasterSkus.length > 0) {
        this.addScanLog("DRAFT_SKU_CHECK", this.selectedOrder.orderNo, "CONFIRMED", "가등록 상품 현장 확인")
      }

      if (this.isExpressMode) {
        try {
          await this.completeExpressPacking({ selectedOrder: this.selectedOrder })
        } catch (err) {
          this.pageState = PAGE_STATE.ERROR
          this.messageType = "negative"
          this.messageText = err?.response?.data?.message || err?.message || "익스프레스 포장 처리에 실패했습니다."
          this.addScanLog("EXPRESS_COMPLETE", this.selectedOrder.orderNo, "FAIL", this.messageText)
          this.playErrorSound()
          this.barcode = ""
          this.hblStartInput = ""
          this.itemBarcodeInput = ""
          this.hblCompleteInput = ""
          this.focusActiveInput(true)
        }
        return
      }

      if (this.restorePersistedWorkflowState()) {
        this.playSuccessSound()
        this.barcode = ""
        this.hblStartInput = ""
        this.itemBarcodeInput = ""
        this.hblCompleteInput = ""
        this.focusActiveInput()
        return
      }

      if (this.selectedOrder.waybillPrinted) {
        this.waybillPrintRequested = true
        this.messageType = "warning"
        this.messageText = `${this.selectedOrder.orderNo} 오더는 이미 운송장이 출력되었고 포장은 완료되지 않은 건입니다. 자동 재출력하지 않습니다. HBL 1차 스캔으로 포장을 계속 진행하세요.`
        this.addScanLog("WAYBILL_PRINT", this.selectedOrder.orderNo, "SKIP", "이미 출력 / 포장 미완료")
        this.playSuccessSound()
        this.barcode = ""
        this.hblStartInput = ""
        this.itemBarcodeInput = ""
        this.hblCompleteInput = ""
        this.focusActiveInput()
        return
      }

      this.messageType = "positive"
      this.messageText = `${this.selectedPartnerLabel} / ${this.lookupRangeLabel} 내 오더가 자동 선택되었습니다. HBL 1차 스캔으로 포장을 시작하세요.`
      this.playSuccessSound()
      this.barcode = ""
      this.hblStartInput = ""
      this.itemBarcodeInput = ""
      this.hblCompleteInput = ""
      this.focusActiveInput()
    },
    validateDraftSkuForms() {
      const invalid = this.draftMasterSkus.find((item) => {
        const form = item.specForm || {}
        return !String(form.model_code || "").trim()
          || !String(form.color || "").trim()
          || !String(form.size || "").trim()
      })

      if (invalid) {
        this.messageType = "negative"
        this.messageText = `${invalid.barcode} 가등록 상품의 모델, 색상, 사이즈를 입력하세요.`
        this.playErrorSound()
        return false
      }

      if (this.isExpressMode) {
        const missingWeight = this.draftMasterSkus.find(item => {
          const raw = String(item.specForm?.weight_kg ?? '').trim()
          return !/^\d+(?:\.\d{1,3})?$/.test(raw) || Number(raw) <= 0 || Number(raw) > 9999.99
        })
        if (missingWeight) {
          this.messageType = 'negative'
          this.messageText = `${missingWeight.barcode} 중량을 0보다 크고 9999.99kg 이하인 숫자로 입력하세요. 소수점은 3자리까지 가능합니다.`
          this.playErrorSound()
          return false
        }
      }

      const numericFields = [
        { key: "weight_kg", label: "중량" },
        { key: "volume_cbm", label: "부피" },
        { key: "length_cm", label: "가로" },
        { key: "width_cm", label: "세로" },
        { key: "height_cm", label: "높이" }
      ]
      const invalidMetric = this.draftMasterSkus
        .map((item) => {
          const field = numericFields.find(({ key }) => {
            const value = item.specForm?.[key]
            if (value === undefined || value === null || String(value).trim() === "") return false
            const n = Number(value)
            return !Number.isFinite(n) || n < 0
          })
          return field ? { item, field } : null
        })
        .find(Boolean)
      if (invalidMetric) {
        this.messageType = "negative"
        this.messageText = `${invalidMetric.item.barcode} 가등록 상품의 ${invalidMetric.field.label} 값을 0 이상 숫자로 입력하세요.`
        this.playErrorSound()
        return false
      }

      return true
    },
    async confirmDraftSkuSpecs() {
      if (this.isSubmittingDraftSkuConfirm) return
      if (!this.validateDraftSkuForms()) return

      this.isSubmittingDraftSkuConfirm = true
      try {
        for (const item of this.draftMasterSkus) {
          const form = item.specForm || {}
          const config = {
            method: "POST",
            url: "/api/fulfillment/packing/draft-master-skus/confirm",
            data: {
              masterSkuId: item.masterSkuId,
              expressMode: this.isExpressMode,
              specDetail: {
                model_code: String(form.model_code || "").trim(),
                color: String(form.color || "").trim(),
                size: String(form.size || "").trim()
              },
              weightKg: form.weight_kg === "" ? null : form.weight_kg,
              volumeCbm: form.volume_cbm === "" ? null : form.volume_cbm,
              lengthCm: form.length_cm === "" ? null : form.length_cm,
              widthCm: form.width_cm === "" ? null : form.width_cm,
              heightCm: form.height_cm === "" ? null : form.height_cm
            },
            name: "[ AXIOS / 포장 / 가등록 상품 확정 ]"
          }
          const res = await this.$http(config)
          if (res?.data?.ok !== true) {
            throw this.expressApiFailure(res?.data, 'DRAFT')
          }
        }

        this.addScanLog("DRAFT_SKU_CHECK", this.selectedOrder?.orderNo || "-", "CONFIRMED", "가등록 상품 스펙 확정")
        this.draftMasterSkus = []
        this.draftSkuConfirmOpen = false
        if (!await this.ensureSelectedOrderSkus(this.isExpressMode ? 'EXPRESS' : 'SELECTION')) {
          this.settleExpressDraftConfirmation(false)
          return
        }
        await this.continueAfterDraftSkuConfirm()
      } catch (err) {
        if (this.isExpressMode) {
          this.showExpressFailure(this.expressFailure(err, 'DRAFT'), this.lastScannedBarcode || '-', 'DRAFT_SKU_CHECK')
        } else {
          this.messageType = "negative"
          this.messageText = err?.response?.data?.message || err?.message || "가등록 상품 확정에 실패했습니다."
          this.addScanLog("DRAFT_SKU_CHECK", this.lastScannedBarcode || "-", "FAIL", this.messageText)
        }
        this.playErrorSound()
      } finally {
        this.isSubmittingDraftSkuConfirm = false
      }
    },
    cancelDraftSkuConfirm() {
      const barcode = this.lastScannedBarcode || "-"
      this.playErrorSound()
      this.draftSkuConfirmOpen = false
      this.settleExpressDraftConfirmation(false)
      if (this.selectedOrder?.orderId) this.pendingSkuResume = {
        orderId: this.selectedOrder.orderId, partnerId: this.selectedPartnerId,
        expressMode: this.isExpressMode, stage: this.isExpressMode ? 'EXPRESS' : 'SELECTION'
      }
      this.messageType = "warning"
      this.messageText = "상품 확인을 중단했습니다. 주문과 스캔 수량은 유지됩니다. ‘상품 확인 후 재개’로 이어서 처리하세요."
      this.addScanLog("DRAFT_SKU_CHECK", barcode, "CANCEL", "가등록 상품 확인 취소")
    },
    /**
     * Title        : 바코드 입력 엔터 처리
     * Status       : Actived
     * Description  : 엔터 입력 시 자동 선택 로직을 실행한다.
     */
    onEnterBarcode() {
      return this.simulateAutoSelect()
    },
    /**
     * Title        : 운송장 출력 요청
     * Status       : Actived
     * Description  : 현재 선택 오더 기준 운송장 출력 요청 상태를 반영한다.
     */
    async printWaybill() {
      if (this.skuRecoveryActive || this.pendingSkuResume) return
      if (!this.selectedOrder) return
      if (this.isSubmittingWaybill) return
      this.waybillPrintConfirmOpen = true
    },
    sanitizePdfFileName(value) {
      return String(value || 'waybill')
        .trim()
        .replace(/[\\/:*?"<>|]+/g, '_')
        .replace(/\s+/g, '_')
    },
    dataUriToBlob(dataUri) {
      const [meta, base64] = String(dataUri || '').split(',')
      const mimeMatch = meta?.match(/data:([^;]+);base64/i)
      const mimeType = mimeMatch?.[1] || 'application/pdf'
      const binary = atob(base64 || '')
      const bytes = new Uint8Array(binary.length)
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i)
      }
      return new Blob([bytes], { type: mimeType })
    },
    async downloadWaybillPdf() {
      if (this.skuRecoveryActive || this.pendingSkuResume) return
      if (!this.selectedOrder || this.isSubmittingWaybill) return

      const orderId = this.selectedOrder.orderId
      if (!orderId) {
        this.messageType = "negative"
        this.messageText = "운송장 PDF 확인 대상 orderId가 없습니다."
        this.addScanLog("WAYBILL_PDF", this.selectedOrder.orderNo, "FAIL", "orderId 없음")
        this.focusActiveInput()
        return
      }

      try {
        this.isSubmittingWaybill = true
        const payload = await this.fetchWaybillPayload(orderId)
        if (!payload) throw new Error("waybill payload missing")

        const pdfBase64 = await this.createWaybillPdfBase64(payload)
        const blob = this.dataUriToBlob(pdfBase64)
        const url = URL.createObjectURL(blob)
        const anchor = document.createElement("a")
        anchor.href = url
        anchor.download = `${this.sanitizePdfFileName(this.selectedOrder.orderNo || orderId)}-waybill.pdf`
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
        setTimeout(() => URL.revokeObjectURL(url), 1000)

        this.messageType = "positive"
        this.messageText = "운송장 PDF를 다운로드했습니다. PDF를 먼저 확인한 뒤 출력하세요."
        this.addScanLog("WAYBILL_PDF", this.selectedOrder.orderNo, "SUCCESS", "PDF 다운로드")
      } catch (err) {
        this.messageType = "negative"
        this.messageText = "운송장 PDF 다운로드에 실패했습니다."
        this.addScanLog("WAYBILL_PDF", this.selectedOrder.orderNo, "FAIL", err?.message || "PDF 다운로드 실패")
        this.$log?.warn?.("[ Packing / 운송장 PDF 다운로드 실패 ]", err)
      } finally {
        this.isSubmittingWaybill = false
        this.focusActiveInput()
      }
    },
    /**
     * Title        : HBL 1차 스캔으로 포장 시작
     * Status       : Actived
     * Description  : HBL 일치 검증 후 포장 시작 상태로 전환한다.
     */
    async startPackingByHbl() {
      if (this.skuRecoveryActive || this.pendingSkuResume) return
      if (this.isStartingPacking) return

      if (!this.selectedOrder) {
        this.messageType = "negative"
        this.messageText = "먼저 오더를 선택하세요."
        this.hblStartInput = ""
        this.playErrorSound()
        return
      }

      if (!this.hblStartInput || !this.hblStartInput.trim()) {
        this.messageType = "negative"
        this.messageText = "HBL 번호를 입력해 주세요."
        this.addScanLog("HBL_START", "", "FAIL", "HBL 미입력")
        this.hblStartInput = ""
        this.playErrorSound()
        this.focusActiveInput()
        return
      }

      if (this.hblStartInput.trim() !== this.selectedOrder.hblNo) {
        this.messageType = "negative"
        this.messageText = "선택된 오더와 HBL이 일치하지 않습니다."
        this.addScanLog("HBL_START", this.hblStartInput.trim(), "FAIL", "HBL 불일치")
        this.hblStartInput = ""
        this.playErrorSound()
        this.focusActiveInput()
        return
      }

      if (this.pageState === PAGE_STATE.COMPLETED) {
        this.messageType = "negative"
        this.messageText = "이미 완료된 오더입니다."
        this.hblStartInput = ""
        this.playErrorSound()
        return
      }

      if (this.selectedOrder.packingCompleted) {
        this.messageType = "negative"
        this.messageText = "이미 포장이 완료된 오더입니다. 포장을 다시 시작할 수 없습니다."
        this.hblStartInput = ""
        this.playErrorSound()
        this.focusActiveInput()
        return
      }

      try {
        this.isStartingPacking = true
        await this.recordPackingProgress("HBL_START", { hblNo: this.hblStartInput.trim() })
        this.pageState = PAGE_STATE.PACKING_STARTED
        this.messageType = "positive"
        this.messageText = "포장을 시작했습니다. 이제 상품 바코드를 스캔해 주세요. 운송장은 최종 무게 입력 후 출력됩니다."
        this.addScanLog("HBL_START", this.selectedOrder.hblNo, "SUCCESS", this.selectedOrder.orderNo)
        this.hblStartInput = ""
        this.playSuccessSound()
      } catch (err) {
        this.messageType = "negative"
        this.messageText = err?.response?.data?.message || err?.message || "포장 시작 처리에 실패했습니다."
        this.addScanLog("HBL_START", this.selectedOrder?.hblNo || "-", "FAIL", this.messageText)
        this.hblStartInput = ""
        this.playErrorSound()
      } finally {
        this.isStartingPacking = false
        this.focusActiveInput()
      }
    },
    /**
     * Title        : 상품 바코드 스캔 반영
     * Status       : Actived
     * Description  : 아이템 수량 누적 및 완료 조건을 계산한다.
     */
    async handleItemScan() {
      if (this.isComposing) return
      if (!this.beginProductScan()) return
      try { return await this.applyItemScan() }
      finally { await this.finishProductScan() }
    },
    async applyItemScan() {
      if (this.skuRecoveryActive || this.pendingSkuResume) return
      if (this.isRecordingProgress) return
      if (!this.canScanItems) {
        this.messageType = "negative"
        this.messageText = "HBL 1차 스캔으로 포장을 시작한 뒤 상품을 스캔하세요."
        this.playErrorSound()
        this.focusActiveInput()
        return
      }

      const scanned = (this.itemBarcodeInput || "").trim()
      if (!scanned) {
        this.messageType = "negative"
        this.messageText = "상품 바코드를 입력해 주세요."
        this.addScanLog("ITEM_SCAN", "", "FAIL", "상품 바코드 미입력")
        this.playErrorSound()
        this.focusActiveInput()
        return
      }

      const matchingItems = this.packingItems.filter((item) => item.barcode === scanned)
      if (!matchingItems.length) {
        this.messageType = "negative"
        this.messageText = "현재 오더에 없는 상품입니다."
        this.addScanLog("ITEM_SCAN", scanned, "FAIL", "오더 미포함 상품")
        this.playErrorSound()
        this.itemBarcodeInput = ""
        this.focusActiveInput()
        return
      }

      const target = matchingItems.find(
        (item) => Number(item.pickedQty || 0) < Number(item.requiredQty || 0)
      )
      if (!target) {
        this.messageType = "negative"
        this.messageText = "초과 스캔입니다. 필요수량을 초과할 수 없습니다."
        this.addScanLog("ITEM_SCAN", scanned, "FAIL", "초과 스캔")
        this.playErrorSound()
        this.itemBarcodeInput = ""
        this.focusActiveInput()
        return
      }

      try {
        this.isRecordingProgress = true
        const progress = await this.recordPackingProgress("ITEM_SCAN", {
          orderItemId: target.id,
          barcode: scanned
        })
        target.pickedQty = Number(progress.pickedQty)
        this.recordSuccessfulItemScan(target)
        this.updateItemStatus(target)
      } catch (err) {
        this.messageType = "negative"
        this.messageText = this.productScanUncertain
          ? '상품 수량 저장 결과가 불명확합니다. 재스캔하지 말고 관리자에게 서버 수량·실물 확인을 요청하세요.'
          : err?.response?.data?.message || err?.message || "상품 스캔 저장에 실패했습니다."
        this.addScanLog("ITEM_SCAN", scanned, "FAIL", this.messageText)
        this.playErrorSound()
        this.itemBarcodeInput = ""
        this.focusActiveInput()
        return
      } finally {
        this.isRecordingProgress = false
      }

      if (this.isAllItemsCompleted) {
        this.pageState = PAGE_STATE.READY_TO_COMPLETE
        this.messageType = "info"
        this.messageText = "전 품목 수량이 충족되었습니다. HBL 재스캔으로 완료하세요."
        this.addScanLog("ITEM_SCAN", scanned, "SUCCESS", "전 품목 충족")
        this.playSuccessSound()
      } else {
        this.messageType = "positive"
        this.messageText = `${target.name} 수량이 반영되었습니다. (${target.pickedQty}/${target.requiredQty})`
        this.addScanLog("ITEM_SCAN", scanned, "SUCCESS", `${target.name} ${target.pickedQty}/${target.requiredQty}`)
        this.playSuccessSound()
      }

      this.itemBarcodeInput = ""
      this.focusActiveInput()
    },
    /**
     * Title        : 수량 수동 조정
     * Status       : Actived
     * Description  : +/- 조작으로 담은수량을 조정하고 상태를 갱신한다.
     * Param (item) : 대상 아이템
     * Param (delta): 증감 수량
     */
    async adjustQty(item, delta) {
      if (this.productScanBusy || this.productScanUncertain || !this.canScanItems || this.isRecordingProgress) return

      const next = item.pickedQty + delta
      if (next < 0) {
        this.messageType = "negative"
        this.messageText = "음수 수량으로 수정할 수 없습니다."
        return
      }
      if (next > item.requiredQty) {
        this.messageType = "negative"
        this.messageText = "필요수량을 초과할 수 없습니다."
        return
      }

      try {
        this.isRecordingProgress = true
        const progress = await this.recordPackingProgress("QTY_ADJUST", {
          orderItemId: item.id,
          barcode: item.barcode,
          delta
        })
        item.pickedQty = Number(progress.pickedQty)
        this.updateItemStatus(item)
      } catch (err) {
        this.messageType = "negative"
        this.messageText = err?.response?.data?.message || err?.message || "수량 조정 저장에 실패했습니다."
        this.addScanLog("QTY_ADJUST", item.barcode, "FAIL", this.messageText)
        this.playErrorSound()
        return
      } finally {
        this.isRecordingProgress = false
      }

      if (this.isAllItemsCompleted) {
        this.pageState = PAGE_STATE.READY_TO_COMPLETE
      } else if (this.pageState === PAGE_STATE.READY_TO_COMPLETE) {
        this.pageState = PAGE_STATE.PACKING_STARTED
      }

      this.messageType = "info"
      this.messageText = `${item.name} 수량이 수동으로 조정되었습니다. (${item.pickedQty}/${item.requiredQty})`
      this.addScanLog("QTY_ADJUST", item.barcode, "SUCCESS", `${delta > 0 ? "+" : "-"}1 수동수정`)
      this.focusActiveInput()
    },
    /**
     * Title        : 최종 HBL 검증
     * Status       : Actived
     * Description  : 완료 조건 및 HBL 일치 여부를 검증하고 최종 무게 입력 단계로 전환한다.
     */
    async completePackingByHbl() {
      if (this.productScanUncertain) return
      if (this.isSubmittingComplete) return

      if (!this.canScanItems) {
        this.messageType = "negative"
        this.messageText = this.completeDisabledReason || "완료할 수 없는 상태입니다."
        this.hblCompleteInput = ""
        this.playErrorSound()
        return
      }

      if (!this.isAllItemsCompleted) {
        this.messageType = "negative"
        this.messageText = "전 품목 필요수량 충족 후 완료할 수 있습니다."
        this.hblCompleteInput = ""
        this.playErrorSound()
        return
      }

      if (!this.hblCompleteInput || !this.hblCompleteInput.trim()) {
        this.messageType = "negative"
        this.messageText = "완료용 HBL 번호를 입력해 주세요."
        this.addScanLog("HBL_COMPLETE", "", "FAIL", "완료 HBL 미입력")
        this.hblCompleteInput = ""
        this.playErrorSound()
        return
      }

      if (this.hblCompleteInput.trim() !== this.selectedOrder.hblNo) {
        this.messageType = "negative"
        this.messageText = "선택된 오더와 HBL이 일치하지 않습니다."
        this.addScanLog("HBL_COMPLETE", this.hblCompleteInput.trim(), "FAIL", "완료 HBL 불일치")
        this.hblCompleteInput = ""
        this.playErrorSound()
        return
      }

      const orderId = this.selectedOrder?.orderId
      if (!orderId) {
        this.messageType = "negative"
        this.messageText = "포장 완료 대상 orderId가 없습니다."
        this.addScanLog("HBL_COMPLETE", this.hblCompleteInput.trim(), "FAIL", "orderId 없음")
        this.hblCompleteInput = ""
        this.playErrorSound()
        return
      }
      if (!this.isSelectedPartnerOrder(this.selectedOrder)) {
        this.messageType = "negative"
        this.messageText = this.getPartnerMismatchMessage()
        this.addScanLog("HBL_COMPLETE", this.hblCompleteInput.trim(), "FAIL", "파트너 불일치")
        this.hblCompleteInput = ""
        this.playErrorSound()
        return
      }

      try {
        this.isSubmittingComplete = true
        await this.recordPackingProgress("HBL_COMPLETE", { hblNo: this.hblCompleteInput.trim() })
      } catch (err) {
        this.messageType = "negative"
        this.messageText = err?.response?.data?.message || err?.message || "최종 HBL 저장에 실패했습니다."
        this.addScanLog("HBL_COMPLETE", this.hblCompleteInput.trim(), "FAIL", this.messageText)
        this.hblCompleteInput = ""
        this.playErrorSound()
        return
      } finally {
        this.isSubmittingComplete = false
      }

      this.pageState = PAGE_STATE.WEIGHT_REQUIRED
      this.messageType = "info"
      this.messageText = "최종 HBL이 확인되었습니다. 박스의 최종 무게를 입력한 뒤 포장을 완료하세요."
      this.addScanLog("HBL_COMPLETE", this.selectedOrder.hblNo, "SUCCESS", "최종 무게 입력 대기")
      this.hblCompleteInput = ""
      this.playSuccessSound()
      this.focusActiveInput()
    },
    async submitPackingCompletion() {
      if (this.productScanUncertain) return
      if (this.isSubmittingComplete) return
      if (!this.isFinalHblConfirmed || !this.isFinalWeightValid) {
        this.messageType = "negative"
        this.messageText = "최종 무게를 0보다 큰 값으로 소수점 둘째 자리까지 입력하세요."
        this.addScanLog("PACKING_WEIGHT", String(this.finalWeightInput || ""), "FAIL", "최종 무게 형식 오류")
        this.playErrorSound()
        this.focusActiveInput()
        return
      }

      const orderId = this.selectedOrder?.orderId
      const weight = this.normalizedFinalWeight
      if (!orderId || !this.isSelectedPartnerOrder(this.selectedOrder)) {
        this.messageType = "negative"
        this.messageText = orderId ? this.getPartnerMismatchMessage() : "포장 완료 대상 orderId가 없습니다."
        this.playErrorSound()
        return
      }

      try {
        this.isSubmittingComplete = true
        const completion = await this.completePackingOrder(orderId, { weight })
        const storedWeight = Number(completion.weight)
        if (!Number.isFinite(storedWeight) || storedWeight !== weight) {
          throw new Error("서버에 저장된 최종 무게를 확인할 수 없습니다.")
        }
        this.selectedOrder.weight = storedWeight
        this.recordRecentPackedOrder(this.selectedOrder, this.packingItems)
        await this.loadRecentPackedOrders()
      } catch (err) {
        this.messageType = "negative"
        this.messageText = err?.response?.data?.message || "포장 완료 서버 반영에 실패했습니다. 다시 시도해 주세요."
        this.addScanLog("PACKING_COMPLETE", String(weight), "FAIL", "완료 API 실패")
        this.$log?.warn?.("[ Packing / 포장완료 API 실패 ]", err)
        this.playErrorSound()
        return
      } finally {
        this.isSubmittingComplete = false
      }

      this.pageState = PAGE_STATE.COMPLETED
      this.messageType = "positive"
      this.messageText = "최종 무게가 저장되었고 포장이 완료되었습니다. 운송장을 출력합니다."
      this.addScanLog("PACKING_COMPLETE", String(weight), "SUCCESS", this.selectedOrder.orderNo)
      this.playSuccessSound()

      try {
        this.isSubmittingWaybill = true
        await this.printWaybillForOrder(this.selectedOrder, {
          successDetail: "최종 무게 반영 자동 출력",
          force: true
        })
        this.messageText = "최종 무게가 저장되었고 포장 완료 및 운송장 출력이 처리되었습니다."
      } catch (err) {
        this.messageType = "warning"
        this.messageText = "포장은 완료되었지만 운송장 출력에 실패했습니다. 수동 출력을 다시 시도해 주세요."
        this.addScanLog("WAYBILL_PRINT", this.selectedOrder.orderNo, "FAIL", err?.message || "최종 운송장 출력 실패")
        this.$log?.warn?.("[ Packing / 최종 운송장 출력 실패 ]", err)
      } finally {
        this.isSubmittingWaybill = false
      }

      this.completionActionModalOpen = true
      this.focusActiveInput()
    },
    /**
     * Title        : 수동조회 모달 열기
     * Status       : Actived
     * Description  : 수동조회 모달을 열고 목록을 조회한다.
     */
    async openManualLookup() {
      if (this.isExpressQueueContextLocked || this.isSelectingNormalOrder) return
      if (this.partnerSelectionRequired) {
        this.messageType = "warning"
        this.messageText = "수동조회 전에 파트너를 먼저 선택하세요."
        this.playErrorSound()
        this.focusActiveInput()
        return
      }

      this.manualLookupKeyword = ""
      this.manualLookupOpen = true
      await this.searchManualLookup()
    },
    /**
     * Title        : 수동조회 모달 닫기
     * Status       : Actived
     * Description  : 모달을 닫고 기본 입력 포커스로 복구한다.
     */
    closeManualLookup() {
      this.manualLookupOpen = false
      this.focusActiveInput()
    },
    /**
     * Title        : 초기화 확인 모달 열기
     * Status       : Actived
     * Description  : 현재 컨텍스트 초기화 전 사용자 확인 모달을 표시한다.
     */
    openResetConfirm() {
      if (!this.canResetByFlow) return
      this.resetConfirmOpen = true
    },
    /**
     * Title        : 초기화 확인 모달 취소
     * Status       : Actived
     * Description  : 초기화 확인을 취소하고 기존 작업 화면으로 복귀한다.
     */
    cancelResetConfirm() {
      this.resetConfirmOpen = false
      this.focusActiveInput()
    },
    /**
     * Title        : 초기화 확인 후 수행
     * Status       : Actived
     * Description  : 확인된 경우 화면 상태를 초기화하고 열려있는 모달을 정리한다.
     */
    confirmResetState() {
      this.resetConfirmOpen = false
      this.completionActionModalOpen = false
      this.resetState()
    },
    /**
     * Title        : 수동조회 오더 선택
     * Status       : Actived
     * Description  : 선택한 오더를 현재 포장 컨텍스트에 반영한다.
     * Param (row)  : 선택된 오더 정보
     */
    async selectManualLookupOrder(row) {
      if (this.isExpressQueueContextLocked || this.isSelectingNormalOrder) return
      if (this.isExpressMode && !this.isSingleExpressItem(row.items)) {
        this.showExpressFailure(this.expressFailure(new Error('일반 포장 대상 주문입니다.'), 'LOOKUP', 'PACKING_EXPRESS_SINGLE_ITEM_REQUIRED'), row.orderNo)
        return
      }
      this.selectedOrder = {
        packingResetId: String(row.packingResetId || "0"),
        orderId: row.orderId || null,
        orderNo: row.orderNo,
        partnerId: row.partnerId || this.selectedPartnerId || null,
        hblNo: row.hblNo,
        customerName: row.customerName,
        destination: row.destination,
        createdAt: row.createdAt,
        selectionReason: row.selectionReason || "수동조회 선택",
        packingStatus: row.packingStatus,
        waybillPrinted: Boolean(row.waybillPrinted),
        packingCompleted: Boolean(row.packingCompleted),
        workflowState: row.workflowState || { started: false, finalHblConfirmed: false }
      }
      this.packingItems = this.buildPackingItems(row.items)
      this.draftMasterSkus = row.draftMasterSkus || []
      this.pageState = PAGE_STATE.ORDER_SELECTED
      this.waybillPrintRequested = false

      this.barcode = ""
      this.hblStartInput = ""
      this.itemBarcodeInput = ""
      this.hblCompleteInput = ""
      this.finalWeightInput = ""
      this.lastScannedBarcode = row.firstBarcode || "-"

      this.addScanLog("MANUAL_SELECT", row.orderNo, "SUCCESS", row.hblNo)
      this.closeManualLookup()

      if (this.selectedOrder.packingCompleted) {
        this.waybillPrintRequested = true
        this.messageType = "negative"
        this.messageText = `${this.selectedOrder.orderNo} 오더는 이미 운송장 출력과 포장이 완료된 건입니다. 자동 출력하지 않습니다.`
        this.addScanLog("WAYBILL_PRINT", this.selectedOrder.orderNo, "SKIP", "출력 완료 / 포장 완료")
        this.playErrorSound()
        this.focusActiveInput()
        return
      }

      if (!await this.ensureSelectedOrderSkus(this.isExpressMode ? 'EXPRESS' : 'SELECTION')) return

      if (this.draftMasterSkus.length > 0) {
        this.messageType = "warning"
        this.messageText = "가등록 상태의 상품이 포함되어 있습니다. 현장 확인 후 계속 진행하세요."
        this.addScanLog("DRAFT_SKU_CHECK", this.lastScannedBarcode, "WAIT", `${this.draftMasterSkus.length}개 가등록 상품 확인 필요`)
        this.playErrorSound()
        this.draftSkuConfirmOpen = true
        return
      }

      this.messageType = "positive"
      this.messageText = `${this.selectedPartnerLabel}에서 수동 조회로 ${row.orderNo} 오더를 선택했습니다. HBL 1차 스캔으로 포장을 시작하세요.`
      await this.continueAfterDraftSkuConfirm()
    },
    /**
     * Title        : 화면 초기화
     * Status       : Actived
     * Description  : 현재 포장 컨텍스트와 입력값을 초기 상태로 되돌린다.
     */
    resetState() {
      if (this.isExpressQueueContextLocked) return
      this.settleExpressDraftConfirmation(false)
      this.backupCompletionOpen = false
      this.backupCompletionTarget = null
      this.backupCompletionError = ""
      this.pageState = PAGE_STATE.IDLE
      this.selectedOrder = null
      this.packingItems = []
      this.messageType = "info"
      this.messageText = this.partnerSelectionRequired
        ? "파트너를 선택한 뒤 바코드를 스캔해 주세요."
        : this.isExpressMode
          ? `${this.selectedPartnerLabel} 익스프레스 모드입니다. 상품 바코드 스캔 시 자동 출력/완료 처리됩니다.`
          : `${this.selectedPartnerLabel} / ${this.lookupRangeLabel} 기준으로 바코드를 다시 스캔해 주세요.`

      this.barcode = ""
      this.hblStartInput = ""
      this.itemBarcodeInput = ""
      this.hblCompleteInput = ""
      this.finalWeightInput = ""
      this.lastScannedBarcode = ""
      this.scanLogs = []
      this.waybillPrintRequested = false
      this.draftSkuConfirmOpen = false
      this.draftMasterSkus = []
      this.isSubmittingDraftSkuConfirm = false
      this.scanMilestoneConfirmOpen = false
      this.scanMilestoneItem = null
      this.focusActiveInput()
    },
    /**
     * Title        : 다음 오더 시작
     * Status       : Actived
     * Description  : 현재 작업을 초기화하고 다음 오더 스캔 상태로 전환한다.
     */
    startNextOrder() {
      this.completionActionModalOpen = false
      this.resetState()
      this.messageType = "info"
      this.messageText = "다음 오더 스캔을 시작하세요."
    },
    handleLookupRangeChange(value) {
      if (this.isExpressQueueContextLocked || this.isSelectingNormalOrder) return
      this.lookupRangePreset = value
      if (this.pageState === PAGE_STATE.IDLE || this.partnerSelectionRequired) {
        this.messageType = "info"
        this.messageText = this.partnerSelectionRequired
          ? "파트너를 선택한 뒤 조회 기간 기준으로 바코드 조회를 진행합니다."
          : `${this.selectedPartnerLabel} / ${this.lookupRangeLabel} 기준으로 바코드 조회를 진행합니다.`
      }
    },
    async loadExpressAvailability() {
      this.expressAvailability = { enabled: false, rules: [] }
      if (!this.selectedPartnerId) {
        this.packingMode = "NORMAL"
        return
      }

      this.expressAvailabilityLoading = true
      try {
        const res = await this.$http({
          method: "POST",
          url: "/api/fulfillment/packing/express-availability",
          data: { partnerId: this.selectedPartnerId },
          name: "[ AXIOS / 포장 / 익스프레스 허용 조회 ]"
        })
        this.expressAvailability = res?.data?.data || { enabled: false, rules: [] }
        if (!this.expressAvailability.enabled) {
          this.packingMode = "NORMAL"
        }
      } catch (err) {
        this.expressAvailability = { enabled: false, rules: [] }
        this.packingMode = "NORMAL"
        this.$log?.warn?.("[ Packing / 익스프레스 허용 조회 실패 ]", err)
      } finally {
        this.expressAvailabilityLoading = false
      }
    },
    handlePackingModeChange(value) {
      if (this.isExpressQueueContextLocked || this.isSelectingNormalOrder) return
      if (value === "EXPRESS" && !this.isExpressAvailable) {
        this.packingMode = "NORMAL"
        this.messageType = "warning"
        this.messageText = "선택한 파트너는 익스프레스 모드를 사용할 수 없습니다."
        return
      }
      this.packingMode = value
      this.resetState()
      this.messageType = "info"
      this.messageText = this.isExpressMode
        ? "익스프레스는 상품 한 행, 수량 1개인 주문만 자동 출력·포장완료합니다. 나머지 주문은 일반 포장으로 진행하세요."
        : "일반 모드입니다. 상품 바코드 선택 후 HBL 스캔으로 포장을 진행합니다."
    },
    async handlePartnerChange(value) {
      if (this.isExpressQueueContextLocked || this.isSelectingNormalOrder) return
      const nextPartnerId = value ? Number(value) : null
      const hasFlow = this.canResetByFlow
      this.selectedPartnerId = Number.isFinite(nextPartnerId) && nextPartnerId > 0 ? nextPartnerId : null
      this.manualLookupRows = []
      this.manualLookupKeyword = ""
      await this.loadExpressAvailability()
      await this.loadRecentPackedOrders()

      if (hasFlow) {
        this.resetState()
        return
      }

      this.messageType = this.partnerSelectionRequired ? "warning" : "info"
      this.messageText = this.partnerSelectionRequired
        ? "파트너를 먼저 선택하세요."
        : `${this.selectedPartnerLabel} 파트너로 포장 대상을 고정했습니다. 바코드를 스캔하세요.`
    },
    async ensurePartnerContext() {
      if (!this.partnerOptions.length) {
        await this.$getPartnerList()
      }

      if (this.isPartnerLocked && this.lockedPartnerId) {
        this.selectedPartnerId = Number(this.lockedPartnerId)
        await this.loadExpressAvailability()
        await this.loadRecentPackedOrders()
        return
      }

      if (!this.selectedPartnerId && this.partnerOptions.length === 1) {
        this.selectedPartnerId = Number(this.partnerOptions[0].value)
      }
      await this.loadExpressAvailability()
      await this.loadRecentPackedOrders()
    }
  },
  /**
   * Title        : 화면 마운트 초기화
   * Status       : Actived
   * Description  : 시계/전역 이벤트/포커스 복구 로직을 등록한다.
   */
  async mounted() {
    this.restoreProductScanState()
    await this.ensurePartnerContext()
    this.expressRecoveryStopped = false
    this.retryPendingExpressFinalizations()
    this.expressRecoveryTimer = window.setInterval(() => this.retryPendingExpressFinalizations(), 5000)
    this.tickSydneyTime()
    this.timerId = window.setInterval(this.tickSydneyTime, 1000)
    this.focusActiveInput()
    window.addEventListener("click", this.handleGlobalClick)
    window.addEventListener("keydown", this.handleGlobalKeydown)
    window.addEventListener("pointerdown", this.handleFirstUserGesture)
    window.addEventListener("keydown", this.handleFirstUserGesture)
    document.addEventListener("visibilitychange", this.handleVisibilityChange)
  },
  /**
   * Title        : 화면 언마운트 정리
   * Status       : Actived
   * Description  : 타이머 및 전역 이벤트 리스너를 해제한다.
   */
  beforeUnmount() {
    this.productScanStopped = true
    if (this.productScanTimer) clearTimeout(this.productScanTimer)
    this.expressRecoveryStopped = true
    this.settleExpressDraftConfirmation(false)
    this.expressScanQueue = []
    if (this.expressRecoveryTimer) window.clearInterval(this.expressRecoveryTimer)
    if (this.timerId) window.clearInterval(this.timerId)
    window.removeEventListener("click", this.handleGlobalClick)
    window.removeEventListener("keydown", this.handleGlobalKeydown)
    window.removeEventListener("pointerdown", this.handleFirstUserGesture)
    window.removeEventListener("keydown", this.handleFirstUserGesture)
    document.removeEventListener("visibilitychange", this.handleVisibilityChange)
    if (this.audioWorkletNode && typeof this.audioWorkletNode.disconnect === "function") {
      this.audioWorkletNode.disconnect()
      this.audioWorkletNode = null
    }
    if (this.audioWorkletGain && typeof this.audioWorkletGain.disconnect === "function") {
      this.audioWorkletGain.disconnect()
      this.audioWorkletGain = null
    }
    if (this.audioWorkletModuleUrl) {
      URL.revokeObjectURL(this.audioWorkletModuleUrl)
      this.audioWorkletModuleUrl = ""
    }
    if (this.audioCtx && typeof this.audioCtx.close === "function") {
      this.audioCtx.close()
      this.audioCtx = null
    }
    this.audioWorkletReady = false
    this.audioUnlocked = false
  }
}
</script>

<template>
  <q-page class="q-pa-lg">
    <div class="row q-mb-lg">
      <div class="col">
        <Title :desc="description">
          <div class="title-meta">
            <span class="meta-chip"><strong>작업자</strong> {{ actorName }}</span>
            <span class="meta-chip"><strong>현장시간</strong> {{ now }}</span>
          </div>
        </Title>
      </div>
    </div>

    <div class="row q-mb-md">
      <div class="col">
        <FlowBanner
          :page-state="pageState"
          :packing-mode="packingMode"
          :is-express-mode="isExpressMode"
          :is-express-available="isExpressAvailable"
          :message-type="messageType"
          :next-action-text="nextActionText"
          :progress-text="progressText"
          :selected-order="selectedOrder"
          :selected-partner-label="selectedPartnerLabel"
          :waybill-print-requested="waybillPrintRequested"
        />
        <q-banner v-if="productScanStatus" class="bg-amber-1 q-mt-sm" role="status" aria-live="polite">
          {{ productScanStatus }}
        </q-banner>
        <q-btn v-if="pendingSkuResume && !skuRecoveryActive" class="q-mt-sm" color="primary" label="상품 확인 후 재개" :disable="isResumingSku" @click="resumeAfterSkuRecovery" />
        <q-btn v-if="pendingExpressClaimReleaseCount" class="q-mt-sm q-ml-sm" outline color="primary"
          :label="`출력 전 해제 ${pendingExpressClaimReleaseCount}건 재확인`" :loading="isRetryingExpressClaimRelease"
          :disable="!!activeExpressOrderId || isAutoPrintingWaybill"
          @click="retryPendingExpressClaimReleases({ adopt: true })" />
        <q-expansion-item icon="help_outline" label="포장 중 상품 등록·연결 안내" dense class="q-mt-sm">
          <div class="q-pa-md text-body2">
            <p>주문 선택 전 처리 가능한 주문이 없는 바코드는 상품 등록·연결 상태를 읽기 전용으로 확인해 안내합니다. 미등록은 입력한 바코드 기준이며, 실제 등록은 풀필먼트 > 신규 등록에서 진행합니다. 이 단계에서는 등록창을 열거나 자동 이동·저장하지 않습니다. 파트너 사용자에게는 자기 상품 연결 범위의 안내만 제공합니다.</p>
            <p>주문 상품을 조회한 결과 공통 상품과 파트너 연결이 모두 없을 때만 미등록 확인창을 엽니다. ‘등록창 열기’는 저장하지 않으며, 필수정보를 입력하고 별도로 저장해야 합니다.</p>
            <p>기존 상품이 있으면 파트너 연결만 저장합니다. 필수정보나 확정 상태가 부족하면 기존 상품을 보완합니다. 이 포장 복구 API는 관리자만 상품 등록·보완·연결을 저장할 수 있습니다.</p>
            <p>취소·저장 실패·조회 오류 시 주문과 스캔 수량을 유지합니다. ‘상품 확인 후 재개’는 같은 주문을 다시 검증하고 중단된 단계만 이어갑니다. 조회 오류는 미등록으로 판단하지 않습니다.</p>
            <p>일반·익스프레스 상품 스캔 처리 중 추가 스캔은 접수·보관하지 않습니다. 결과가 화면에 반영된 후 2초 동안 같은 상품과 다른 상품 모두 차단합니다. 안내가 사라지면 다시 스캔하세요. HBL 확인 스캔에는 이 대기 시간을 적용하지 않습니다.</p>
            <p>상품 등록·확인 중에도 후속 스캔은 보관하지 않습니다. 현재 주문의 확인·복구를 마친 뒤 다음 상품을 다시 스캔하세요. 수량 저장 결과가 불명확하면 2초 후에도 재스캔·수량 조정·완료가 차단되며, 관리자가 서버 수량과 실물을 확인해야 합니다. 이 화면에서는 불명확한 저장을 재전송하거나 강제로 해제하지 않습니다.</p>
            <p>2초 차단은 현재 화면의 입력 보호입니다. 다른 탭·단말의 스캔을 함께 막지 못하므로 한 주문은 한 작업자가 처리하세요.</p>
            <p class="q-mb-none">출력 접수 성공은 실제 출력 완료와 다릅니다. 출력 결과가 불명확하면 자동 재출력하지 않습니다. 실물 출력과 주문 상태를 관리자가 확인해야 합니다.</p>
          </div>
        </q-expansion-item>
      </div>
    </div>

    <div class="row q-mb-sm">
      <div class="col">
        <div class="lookup-range-bar">
          <div class="lookup-range-copy">
            <span class="lookup-range-title">조회 기간</span>
            <span class="lookup-range-desc">바코드 스캔과 수동조회에 동일하게 적용됩니다.</span>
          </div>
          <q-btn-toggle
            :model-value="lookupRangePreset"
            :options="lookupRangeOptions"
            unelevated
            no-caps
            toggle-color="primary"
            color="white"
            text-color="grey-8"
            :disable="isExpressQueueContextLocked || isSelectingNormalOrder"
            @update:model-value="handleLookupRangeChange"
          />
        </div>

        <div class="partner-context-bar q-mt-sm">
          <div class="partner-context-copy">
            <span class="partner-context-title">작업 파트너</span>
            <span class="partner-context-desc">파트너를 먼저 고르면 바코드 스캔과 수동조회가 해당 파트너 오더로 제한됩니다.</span>
          </div>
          <div class="partner-context-control">
            <q-select
              :model-value="selectedPartnerId"
              :options="partnerOptions"
              label="파트너 선택"
              outlined
              dense
              emit-value
              map-options
              :disable="isPartnerLocked || isExpressQueueContextLocked || isSelectingNormalOrder"
              @update:model-value="handlePartnerChange"
            />
            <q-badge
              rounded
              :color="partnerSelectionRequired ? 'amber-8' : 'indigo-7'"
              text-color="white"
              class="partner-status-badge"
            >
              {{ partnerSelectionRequired ? "선택 필요" : `${selectedPartnerLabel} 고정` }}
            </q-badge>
            <q-btn-toggle
              :model-value="packingMode"
              :options="packingModeOptions"
              dense
              unelevated
              :toggle-color="packingModeToggleColor"
              color="white"
              text-color="grey-8"
              :loading="expressAvailabilityLoading"
              :disable="isExpressQueueContextLocked || isSelectingNormalOrder"
              @update:model-value="handlePackingModeChange"
            />
            <q-badge
              rounded
              :color="isExpressAvailable ? 'green-7' : 'grey-6'"
              text-color="white"
              class="partner-status-badge"
            >
              {{ isExpressAvailable ? "익스프레스 가능" : "일반만 가능" }}
            </q-badge>
          </div>
        </div>
      </div>
    </div>

    <div class="row q-col-gutter-sm">
      <div class="col-12 col-lg-7">
        <div class="row q-col-gutter-sm q-mb-sm top-equal-row">
          <div :class="isExpressMode ? 'col-12 col-md-5' : 'col-12 col-md-4'">
            <BarcodeScanCard
              ref="barcodeInput"
              :barcode="barcode"
              :scan-blocked="productScanBlocked || skuRecoveryActive || !!pendingSkuResume"
              @update:barcode="updateProductScanInput('barcode', $event)"
              @scan-key="handleProductScanKey"
              v-model:isComposing="isComposing"
              :last-scanned-barcode="lastScannedBarcode"
              :selected-partner-label="selectedPartnerLabel"
              :partner-selection-required="partnerSelectionRequired"
              :is-express-mode="isExpressMode"
              :can-reset="canResetByFlow"
              @scan="onEnterBarcode"
              @open-manual="openManualLookup"
              @reset="openResetConfirm"
            />
          </div>

          <div :class="isExpressMode ? 'col-12 col-md-7' : 'col-12 col-md-8'">
            <OrderStartCard
              ref="hblStartInputRef"
              v-model:hblStartInput="hblStartInput"
              v-model:isComposing="isComposing"
              :selected-order="selectedOrder"
              :can-start-by-hbl="canStartByHbl"
              :is-starting-packing="isStartingPacking"
              :is-submitting-waybill="isSubmittingWaybill"
              :sku-recovery-blocked="skuRecoveryActive || !!pendingSkuResume"
              :is-express-mode="isExpressMode"
              @print-waybill="printWaybill"
              :can-complete-backup="canCompleteBackup"
              :is-submitting-backup-completion="isSubmittingBackupCompletion"
              @complete-backup="openBackupCompletion"
              @download-waybill="downloadWaybillPdf"
              @start-packing="startPackingByHbl"
            />
          </div>
        </div>

        <PickingListPanel
          v-if="!isExpressMode"
          ref="pickingListPanelRef"
          :selected-order="selectedOrder"
          :progress-text="progressText"
          :item-barcode-input="itemBarcodeInput"
          :scan-blocked="productScanBlocked || skuRecoveryActive || !!pendingSkuResume"
          @scan-key="handleProductScanKey"
          :hbl-complete-input="hblCompleteInput"
          :can-scan-items="canScanItems"
          :can-complete-packing="canCompletePacking"
          :is-submitting-complete="isSubmittingComplete"
          :complete-disabled-reason="completeDisabledReason"
          :final-weight-input="finalWeightInput"
          :is-final-hbl-confirmed="isFinalHblConfirmed"
          :can-submit-packing-completion="canSubmitPackingCompletion"
          :is-all-items-completed="isAllItemsCompleted"
          :pending-items="pendingItems"
          :packing-items="packingItems"
          @update:itemBarcodeInput="updateProductScanInput('itemBarcodeInput', $event)"
          @update:hblCompleteInput="hblCompleteInput = $event"
          @update:finalWeightInput="finalWeightInput = $event"
          @update:isComposing="isComposing = $event"
          @scan-item="handleItemScan"
          @complete-packing="completePackingByHbl"
          @submit-completion="submitPackingCompletion"
          @adjust-qty="adjustQty"
        />
      </div>

      <div class="col-12 col-lg-5">
        <ScanLogsPanel :logs="scanLogs" />

        <div
          v-if="recentPackedLoading || recentPackedOrders.length > 0"
          class="recent-packed-panel q-mt-sm">
          <div class="recent-packed-header">
            <div>
              <div class="recent-packed-title">최근 포장완료</div>
              <div class="recent-packed-caption">
                최근 24시간 콘솔대기 {{ recentPackedSummary.ready }}건 / 전체 {{ recentPackedSummary.total }}건
              </div>
            </div>
            <q-spinner
              v-if="recentPackedLoading"
              color="primary"
              size="24px" />
            <q-btn
              v-else
              color="primary"
              outline
              dense
              icon="open_in_new"
              label="통합조회"
              @click="goParcelInfoWithRecentPacked" />
          </div>

          <div
            v-if="recentPackedOrders.length > 0"
            class="recent-packed-list">
            <div
              v-for="item in recentPackedOrders.slice(0, 5)"
              :key="item.orderNo"
              class="recent-packed-item">
              <div class="recent-packed-main">
                <span class="recent-packed-order">{{ item.orderNo }}</span>
                <q-badge color="yellow-9" text-color="white">콘솔대기</q-badge>
              </div>
              <div class="recent-packed-sub">
                {{ item.hblNo }} / {{ item.itemNames }}
              </div>
            </div>
          </div>
          <div
            v-else-if="!recentPackedLoading"
            class="recent-packed-empty">
            최근 24시간 내 콘솔대기 포장완료 건이 없습니다.
          </div>
        </div>

      </div>
    </div>

    <ManualLookupDialog
      v-model="manualLookupOpen"
      v-model:keyword="manualLookupKeyword"
      :rows="filteredManualLookupRows"
      :loading="manualLookupLoading"
      @search="searchManualLookup"
      @select="selectManualLookupOrder"
      @close="closeManualLookup"
    />

    <CompletionActionDialog
      v-model="completionActionModalOpen"
      @reset="openResetConfirm"
      @next-order="startNextOrder"
    />

    <ResetConfirmDialog
      v-model="resetConfirmOpen"
      @cancel="cancelResetConfirm"
      @confirm="confirmResetState"
    />

    <WaybillPrintConfirmDialog
      v-model="waybillPrintConfirmOpen"
      :selected-order="selectedOrder"
      @cancel="cancelWaybillPrintConfirm"
      @confirm="printWaybillAfterConfirm"
    />

    <q-dialog v-model="backupCompletionOpen" persistent>
      <q-card style="width: 480px; max-width: 95vw">
        <q-card-section>
          <div class="text-h6">임시콘솔 + 포장완료</div>
          <div class="text-body2 q-mt-sm">오더: {{ backupCompletionTarget?.orderNo }}</div>
          <div class="text-body2">HBL: {{ backupCompletionTarget?.hblNo }}</div>
          <div class="text-body2 q-mt-sm">
            오류 복구용 수동 완료입니다. 상품과 포장이 완료됐는지 확인하고 최종 중량을 입력하세요.
            완료하면 임시콘솔 대기로 저장됩니다. 운송장 출력은 별도로 확인하세요.
          </div>
          <q-input
            v-model="backupCompletionWeight"
            label="최종 중량 (kg)"
            inputmode="decimal"
            :disable="isSubmittingBackupCompletion"
            hint="0.01~9999.99kg, 소수점 둘째 자리까지"
            class="q-mt-md"
          />
          <div v-if="backupCompletionError" role="alert" class="text-negative q-mt-md">{{ backupCompletionError }}</div>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="취소" :disable="isSubmittingBackupCompletion" @click="cancelBackupCompletion" />
          <q-btn color="positive" label="확인 후 완료" :loading="isSubmittingBackupCompletion"
            :disable="normalizedBackupCompletionWeight === null || isSubmittingBackupCompletion"
            @click="submitBackupCompletion" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <PackingSkuDialog ref="skuRecovery" @active="skuRecoveryActive = $event" @queue-scan="queueRecoveryScan" />
    <q-dialog v-model="scanMilestoneConfirmOpen" persistent>
      <q-card class="scan-milestone-card">
        <q-card-section>
          <div class="text-h6">상품 스캔 확인</div>
          <div class="text-body2 q-mt-sm">
            성공 스캔 {{ scanMilestoneItem?.count || 0 }}번째 상품입니다. 상품명을 확인한 뒤 다음 스캔을 진행하세요.
          </div>
        </q-card-section>

        <q-card-section class="q-pt-none">
          <div class="scan-milestone-product">
            <div class="text-caption text-grey-7">스캔된 상품명</div>
            <div class="scan-milestone-name">{{ scanMilestoneItem?.name || "-" }}</div>
            <div class="text-caption text-grey-7 q-mt-xs">
              {{ scanMilestoneItem?.barcode || "-" }} / {{ scanMilestoneItem?.pickedQty || 0 }}개 담음
              <span v-if="scanMilestoneItem?.requiredQty">/ 필요 {{ scanMilestoneItem.requiredQty }}개</span>
            </div>
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn
            unelevated
            color="primary"
            label="확인"
            @click="confirmScanMilestone"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <q-dialog v-model="draftSkuConfirmOpen" persistent>
      <q-card class="draft-sku-confirm">
        <q-card-section>
          <div class="text-h6">가등록 상품 스펙 확정</div>
          <div class="text-body2 q-mt-sm">
            {{ isExpressMode
              ? '현장 실측 스펙과 중량을 확정하면 같은 오더의 운송장 출력과 포장완료가 자동으로 이어집니다.'
              : '선택된 오더에 가등록 상태의 상품 바코드가 포함되어 있습니다. 현장 실측값을 입력해 확정한 뒤 포장을 진행하세요.' }}
          </div>
          <div v-if="messageType === 'negative'" role="alert" class="text-negative q-mt-sm">{{ messageText }}</div>
        </q-card-section>

        <q-card-section class="q-pt-none">
          <div class="draft-sku-list">
            <div
              v-for="item in draftMasterSkus"
              :key="`${item.masterSkuId || ''}-${item.barcode}`"
              class="draft-sku-item"
            >
              <div class="draft-sku-item__header">
                <div>
                  <div class="draft-sku-item__name">{{ item.displayName }}</div>
                  <div class="draft-sku-item__meta">{{ item.masterSkuCode }} / {{ item.barcode }}</div>
                </div>
                <q-badge color="orange-8" outline>가등록</q-badge>
              </div>

              <div class="draft-sku-section">
                <div class="draft-sku-section__title">규격 상세</div>
                <div class="row q-col-gutter-sm">
                  <div class="col-12 col-sm-4">
                    <q-input
                      v-model="item.specForm.model_code"
                      dense
                      outlined
                      label="모델"
                    />
                  </div>
                  <div class="col-12 col-sm-4">
                    <q-input
                      v-model="item.specForm.color"
                      dense
                      outlined
                      label="색상"
                    />
                  </div>
                  <div class="col-12 col-sm-4">
                    <q-input
                      v-model="item.specForm.size"
                      dense
                      outlined
                      label="사이즈"
                    />
                  </div>
                </div>
              </div>

              <div class="draft-sku-section">
                <div class="draft-sku-section__title">물류 기본값</div>
                <div class="draft-logistics-grid">
                  <div class="draft-logistics-group">
                    <div class="draft-logistics-group__label">실측 값</div>
                    <div class="row q-col-gutter-sm">
                      <div class="col-12 col-sm-6">
                        <q-input
                          v-model="item.specForm.weight_kg"
                          dense
                          outlined
                          type="number"
                          min="0"
                          step="0.001"
                          label="중량(kg)"
                        />
                      </div>
                      <div class="col-12 col-sm-6">
                        <q-input
                          v-model="item.specForm.volume_cbm"
                          dense
                          outlined
                          type="number"
                          min="0"
                          step="0.001"
                          label="부피(cbm)"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="draft-logistics-group draft-logistics-group--wide">
                    <div class="draft-logistics-group__label">치수 및 항공 부피무게</div>
                    <div class="row q-col-gutter-sm items-end">
                      <div class="col-12 col-sm-4 col-md">
                        <q-input
                          v-model="item.specForm.length_cm"
                          dense
                          outlined
                          type="number"
                          min="0"
                          step="0.001"
                          label="가로(cm)"
                        />
                      </div>
                      <div class="col-12 col-sm-4 col-md">
                        <q-input
                          v-model="item.specForm.width_cm"
                          dense
                          outlined
                          type="number"
                          min="0"
                          step="0.001"
                          label="세로(cm)"
                        />
                      </div>
                      <div class="col-12 col-sm-4 col-md">
                        <q-input
                          v-model="item.specForm.height_cm"
                          dense
                          outlined
                          type="number"
                          min="0"
                          step="0.001"
                          label="높이(cm)"
                        />
                      </div>
                      <div class="col-12 col-sm-6 col-md">
                        <q-input
                          :model-value="calculateDraftFlightWeight(item)"
                          dense
                          outlined
                          readonly
                          label="항공 부피무게(kg)"
                        />
                      </div>
                      <div class="col-12 col-sm-6 col-md-auto">
                        <q-btn
                          outline
                          color="primary"
                          icon="flight"
                          label="중량 반영"
                          class="draft-sku-flight-btn"
                          @click="applyDraftFlightWeight(item)"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn
            flat
            label="취소"
            color="grey-7"
            :disable="isSubmittingDraftSkuConfirm"
            @click="cancelDraftSkuConfirm"
          />
          <q-btn
            unelevated
            :label="isExpressMode ? '확정 후 자동 출력·포장완료' : '확정 후 진행'"
            color="warning"
            :loading="isSubmittingDraftSkuConfirm"
            @click="confirmDraftSkuSpecs"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <div class="packing-waybill-capture">
      <PdfPreview
        ref="packingWaybillPreview"
        :parcel-list="printWaybillParcelList"
        :hide-background="true"
      />
    </div>
  </q-page>
</template>

<style scoped lang="scss">
.section-title {
  font-size: 1rem;
  font-weight: 700;
}

.title-meta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.meta-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 999px;
  background: #f7f9fb;
  font-size: 12px;
  color: #374151;
}

.title-alert {
  display: inline-block;
  max-width: 360px;
  font-size: 12px;
  color: #555;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.lookup-range-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border: 1px solid #d9e2ec;
  border-radius: 14px;
  background: linear-gradient(135deg, #f8fbff 0%, #eef5fb 100%);
}

.partner-context-bar {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 16px;
  padding: 16px 18px;
  border: 1px solid #d8deea;
  border-radius: 16px;
  background:
    radial-gradient(circle at top left, rgba(29, 78, 216, 0.1), transparent 34%),
    linear-gradient(135deg, #ffffff 0%, #f4f7fb 100%);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
}

.partner-context-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.partner-context-title {
  font-size: 15px;
  font-weight: 700;
  color: #132238;
}

.partner-context-desc {
  font-size: 12px;
  color: #536273;
}

.partner-context-control {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  min-width: 320px;
}

.partner-context-control :deep(.q-field) {
  min-width: 220px;
  background: #fff;
}

.partner-status-badge {
  min-height: 30px;
}

.lookup-range-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.lookup-range-title {
  font-size: 14px;
  font-weight: 700;
  color: #16324f;
}

.lookup-range-desc {
  font-size: 12px;
  color: #4a5b6d;
}

.top-equal-row > [class*="col-"] {
  display: flex;
}

.top-equal-row > [class*="col-"] .comp-box {
  width: 100%;
  height: 100%;
}

.compact-btn {
  min-height: 34px;
}

.packing-waybill-capture {
  position: fixed;
  left: 0;
  top: 0;
  width: 17cm;
  height: auto;
  overflow: visible;
  pointer-events: none;
  opacity: 0;
}

.draft-sku-confirm {
  width: min(920px, 94vw);
}

.draft-sku-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.draft-sku-item {
  padding: 14px;
  border: 1px solid #d8e2ec;
  border-radius: 8px;
  background: #fff;
}

.draft-sku-item__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #edf1f5;
}

.draft-sku-item__name {
  color: #132238;
  font-size: 15px;
  font-weight: 700;
}

.draft-sku-item__meta {
  margin-top: 3px;
  color: #667085;
  font-size: 12px;
}

.draft-sku-section {
  margin-top: 14px;
}

.draft-sku-section__title {
  margin-bottom: 8px;
  color: #344054;
  font-size: 13px;
  font-weight: 700;
}

.draft-logistics-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.draft-logistics-group {
  padding: 12px;
  border: 1px solid #e4ebf3;
  border-radius: 8px;
  background: #f8fafc;
}

.draft-logistics-group__label {
  margin-bottom: 8px;
  color: #52616f;
  font-size: 12px;
  font-weight: 700;
}

.draft-sku-flight-btn {
  width: 100%;
  min-height: 40px;
}

.scan-milestone-card {
  width: min(480px, 92vw);
}

.scan-milestone-product {
  padding: 14px;
  border: 1px solid #d8e2ec;
  border-radius: 8px;
  background: #f8fafc;
}

.scan-milestone-name {
  margin-top: 4px;
  font-size: 18px;
  font-weight: 700;
  color: #132238;
}

.recent-packed-panel {
  padding: 14px;
  border: 1px solid #d8e2ec;
  border-radius: 8px;
  background: #fff;
}

.recent-packed-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.recent-packed-title {
  color: #132238;
  font-size: 15px;
  font-weight: 700;
}

.recent-packed-caption {
  margin-top: 2px;
  color: #667085;
  font-size: 12px;
}

.recent-packed-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
}

.recent-packed-item {
  padding: 9px 10px;
  border: 1px solid #edf1f5;
  border-radius: 6px;
  background: #fafafa;
}

.recent-packed-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.recent-packed-order {
  color: #1f2d3d;
  font-size: 13px;
  font-weight: 700;
}

.recent-packed-sub {
  margin-top: 4px;
  color: #667085;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.recent-packed-empty {
  margin-top: 12px;
  padding: 10px;
  border: 1px dashed #d8e2ec;
  border-radius: 6px;
  color: #667085;
  font-size: 12px;
  text-align: center;
}

@media (max-width: 1023px) {
  .title-meta {
    justify-content: flex-end;
  }

  .lookup-range-bar {
    flex-direction: column;
    align-items: flex-start;
  }

  .partner-context-bar {
    flex-direction: column;
    align-items: flex-start;
  }

  .partner-context-control {
    width: 100%;
    min-width: 0;
  }

  .partner-context-control :deep(.q-field) {
    flex: 1 1 auto;
    min-width: 0;
  }
}

@media (max-width: 599px) {
  .title-meta {
    justify-content: flex-start;
  }

  .title-alert {
    max-width: 180px;
  }

  .lookup-range-bar :deep(.q-btn-group) {
    width: 100%;
  }

  .lookup-range-bar :deep(.q-btn) {
    flex: 1 1 0;
  }

  .partner-context-control {
    flex-direction: column;
    align-items: stretch;
  }

}
</style>
